/**
 * Flagship Product — JS
 *
 * <flagship-product> IS the variant engine: the single source of truth for the
 * current {color, gender, size} selection. It resolves the variant client-side
 * from the section's FlagshipData JSON (no server round-trip, no theme
 * <variant-picker>), writes the cart form's `name="id"`, derives ATC state +
 * per-option availability (funnel: colour → gender given colour → size given
 * colour + gender), flips the palette via ONE `data-color` attribute (CSS in
 * src/application.css does the rest), updates the URL, and broadcasts
 * `flagship:variant:change` (+ a theme-compatible `on:variant:change` for
 * @app blocks).
 *
 * Views (hero crossfade, carousel filter, sticky bar, drawer, secondary CTAs)
 * are controls that write to the engine and readers of its events — none of
 * them derives state on its own.
 *
 * Editor-safe by construction: everything binds in connectedCallback through
 * one AbortController and dies in disconnectedCallback, so the theme editor
 * can re-render the section freely (plan 0.6).
 */
(() => {
  // Debug gate: append ?flagshipdebug to the URL to enable verbose logging.
  const FLAGSHIP_DEBUG = new URLSearchParams(location.search).has(
    "flagshipdebug",
  );

  const handleize = (str) =>
    String(str ?? "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  // Suppresses the synthetic click that follows a touch drag (see drag code).
  let flagshipNoClick = 0;

  // ── The engine ───────────────────────────────────────────────────────────

  class FlagshipProduct extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      const { signal } = this._ac;
      this._log = FLAGSHIP_DEBUG
        ? (...args) =>
            console.log("[Flagship:" + this.dataset.sectionId + "]", ...args)
        : () => {};

      if (!this._parseData()) return;
      this._readInitialSelection();
      this._variant = this._resolve();

      // Controls: the option radios (hero colour, highlights gender/size)
      // write to the engine. One delegated listener.
      this.addEventListener(
        "change",
        (evt) => {
          const input =
            evt.target && evt.target.closest
              ? evt.target.closest(".js-option")
              : null;
          if (!input) return;
          const fieldset = input.closest(".option-selector");
          if (!fieldset) return;
          this.setOptionByName(fieldset.dataset.option, input.value);
        },
        { signal },
      );

      // The cart form's own submit (the buy-box button, Enter in the form)
      // is the flagship's request too, captured here ahead of the vendor
      // <product-form>, whose listener sits on the form itself (see
      // submitMainForm for why the vendor path is never taken).
      this.addEventListener(
        "submit",
        (evt) => {
          if (!evt.target.closest || !evt.target.closest(".js-product-form"))
            return;
          evt.preventDefault();
          evt.stopImmediatePropagation();
          submitMainForm(this);
        },
        { capture: true, signal },
      );

      // Views.
      initColorSwitching(this);
      initSecondaryCtas(this);
      initStickyBar(this);
      initSizeDrawer(this);
      initDrawerDrag(this);
      applyMediaColorMap(this);
      initGalleryCarousel(this);
      initGalleryVideos(this);
      initCarouselLightbox(this);
      initLookbook(this);
      initStoryHeader(this);
      initFrameHero(this);
      initSimpleHero(this);
      initSizeGuide(this);
      initReviewsLink(this);
      initPriceRow(this);
      initColorName(this);
      warmHeroImages(this);
      initPillFit(this);

      // First paint: recompute availability + ATC + gallery from the
      // server-rendered radios (no event dispatch — views seed themselves).
      this._sync(null, { dispatch: false });
      filterGalleryByColor(this, this.selection.color);

      this._log("engine ready", {
        selection: this.selection,
        variant: this._variant,
      });
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
    }

    // ── Public API (the getFlagshipSelection contract lives on top of this) ─

    get selection() {
      return {
        color: this._sel.color ?? null,
        gender: this._sel.gender ?? null,
        size: this._sel.size ?? null,
      };
    }

    get variant() {
      return this._variant;
    }

    get facts() {
      return this._facts;
    }

    /** Ready = a size is chosen AND the resolved variant is purchasable. */
    isReady() {
      return !!(this._sel.size && this._variant && this._variant.available);
    }

    setOptionByName(name, value) {
      const role = this._roleForName(name);
      if (role) this.setOption(role, value);
    }

    /**
     * The one write path. Applies the auto-select policy (plan 0.5): a colour
     * or gender change that invalidates the chosen size CLEARS the size (ATC
     * returns to "Select Size") — no silent size substitution.
     */
    setOption(role, value) {
      if (this._sel[role] === value) return;
      // Views that measure layout do it here, before _sync's writes restyle
      // the page: a read after them forces that whole restyle inside the
      // click, which is most of a colour pick's INP.
      this.dispatchEvent(
        new CustomEvent("flagship:variant:beforechange", {
          detail: { role, value },
        }),
      );
      this._sel[role] = value;
      if (role !== "size" && this._sel.size) {
        if (!this.availableValues("size").has(this._sel.size)) {
          this._log("size cleared — no longer available", this._sel.size);
          this._sel.size = null;
        }
      }
      this._variant = this._resolve();
      this._sync(role, { dispatch: true });
    }

    /**
     * Values of `role` currently pickable, given the selections of the options
     * BEFORE it in the funnel (colour is always pickable; gender is filtered
     * by colour; size by colour + gender). Only in-stock variants count.
     */
    availableValues(role) {
      const out = new Set();
      const idx = this._facts.byRole[role];
      if (idx == null) return out;
      for (const v of this._facts.variants) {
        if (!v.available) continue;
        let ok = true;
        for (let j = 0; j < idx; j += 1) {
          const jRole = this._facts.options[j].role;
          const selVal = this._sel[jRole];
          if (selVal && v.options[j] !== selVal) {
            ok = false;
            break;
          }
        }
        if (ok) out.add(v.options[idx]);
      }
      return out;
    }

    // ── Internals ────────────────────────────────────────────────────────────

    _parseData() {
      const el = this.querySelector("[data-flagship-data]");
      if (!el) return false;
      let data;
      try {
        data = JSON.parse(el.textContent);
      } catch (err) {
        this._log("FlagshipData parse failed", err);
        return false;
      }
      const byRole = {};
      data.options.forEach((opt, i) => {
        opt.index = i;
        if (!opt.role || opt.role === "other") opt.role = "option" + i;
        byRole[opt.role] = i;
      });
      this._facts = { ...data, byRole };
      return true;
    }

    _roleForName(name) {
      const lower = String(name ?? "").toLowerCase();
      const opt = this._facts.options.find(
        (o) => o.name.toLowerCase() === lower,
      );
      return opt ? opt.role : null;
    }

    /**
     * Server-rendered checked radios seed the state; the island's `initial`
     * backstops it. The backstop is the whole seed on the story layout, where
     * colour is picked with buttons at every width and there is no colour
     * radio to read — it is the deep-linked variant's colour when the URL
     * carries one, so ?variant= still paints the right page there.
     */
    _readInitialSelection() {
      this._sel = {};
      this.querySelectorAll(".option-selector").forEach((fieldset) => {
        const role = this._roleForName(fieldset.dataset.option);
        const checked = fieldset.querySelector(".js-option:checked");
        if (role && checked) this._sel[role] = checked.value;
      });
      const initial = this._facts.initial || {};
      if (!this._sel.color && initial.color) this._sel.color = initial.color;
      if (!this._sel.gender && initial.gender) {
        this._sel.gender = initial.gender;
      }
    }

    /** Exact variant for the current selection; null while incomplete. */
    _resolve() {
      const vals = this._facts.options.map((o) => this._sel[o.role] ?? null);
      if (vals.includes(null)) return null;
      return (
        this._facts.variants.find((v) =>
          v.options.every((ov, i) => ov === vals[i]),
        ) || null
      );
    }

    /** One pass that makes every reader agree with the engine. */
    _sync(changedRole, { dispatch }) {
      const variant = this._variant;

      // (1) Radios reflect the selection (a cleared size unchecks its group).
      this.querySelectorAll(".option-selector").forEach((fieldset) => {
        const role = this._roleForName(fieldset.dataset.option);
        if (!role) return;
        const selVal = this._sel[role] ?? null;
        fieldset.querySelectorAll(".js-option").forEach((input) => {
          input.checked = selVal !== null && input.value === selVal;
        });
      });

      // (2) Per-option availability (funnel) → input class + label attribute.
      this._facts.options.forEach((opt) => {
        const fieldset = this.querySelector(
          `.option-selector[data-option="${CSS.escape(opt.name)}"]`,
        );
        if (!fieldset) return;
        const avail = this.availableValues(opt.role);
        fieldset.querySelectorAll(".js-option").forEach((input) => {
          const isUnavailable = !avail.has(input.value);
          input.classList.toggle("is-unavailable", isUnavailable);
          const label = this.querySelector(
            `label[for="${CSS.escape(input.id)}"]`,
          );
          if (label) label.toggleAttribute("data-unavailable", isUnavailable);
        });
      });

      // (3) Cart form id + ATC state — a pure function of the resolved variant.
      const idInput = this.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant ? variant.id : "";

      const atcBtn = this.querySelector('[name="add"]');
      if (atcBtn) {
        let text;
        let disabled;
        if (!variant) {
          text = atcBtn.dataset.preselectionText || "Select Size";
          disabled = true;
        } else if (variant.available) {
          text = atcBtn.dataset.addToCartText || "Add to Cart";
          disabled = false;
        } else {
          text = atcBtn.dataset.soldoutText || "Sold out";
          disabled = true;
        }
        if (atcBtn.textContent.trim() !== text) atcBtn.textContent = text;
        if (disabled) atcBtn.setAttribute("aria-disabled", "true");
        else atcBtn.removeAttribute("aria-disabled");
      }

      // (4) Palette + gender copy: one attribute write each; CSS does the rest.
      if (this._sel.color) this.dataset.color = handleize(this._sel.color);
      if (this._sel.gender) this.dataset.gender = handleize(this._sel.gender);

      // (5) URL reflects the resolved variant (deep-linkable), else no param.
      // Not on the showroom: three engines share one collection URL there,
      // and a ?variant= on a collection page names nothing.
      if (this.dataset.layout !== "showroom") {
        try {
          const url = new URL(location.href);
          if (variant) url.searchParams.set("variant", variant.id);
          else url.searchParams.delete("variant");
          history.replaceState(history.state, "", url);
        } catch {
          /* URL/History unavailable — non-fatal */
        }
      }

      if (!dispatch) return;

      // (6) Announce the new state for screen readers (visually hidden
      // aria-live region in the section markup). Strings reuse the ATC
      // button's server-rendered data-* labels so they stay localised.
      const live = this.querySelector("[data-flagship-announce]");
      if (live && atcBtn) {
        const parts = [];
        if (this._sel.color) parts.push(this._sel.color);
        if (this._sel.gender) parts.push(this._sel.gender);
        if (this._sel.size) parts.push("size " + this._sel.size);
        let state;
        if (!variant) {
          state = atcBtn.dataset.preselectionText || "Select Size";
        } else if (variant.available) {
          state = variant.price
            ? variant.price +
              ", " +
              (atcBtn.dataset.addToCartText || "Add to Cart")
            : atcBtn.dataset.addToCartText || "Add to Cart";
        } else {
          state = atcBtn.dataset.soldoutText || "Sold out";
        }
        live.textContent =
          parts.join(", ") + (parts.length ? " — " : "") + state;
      }

      const selectedOptions = this._facts.options.map(
        (o) => this._sel[o.role] ?? null,
      );
      this.dispatchEvent(
        new CustomEvent("flagship:variant:change", {
          bubbles: true,
          detail: {
            engine: this,
            variant,
            selection: this.selection,
            changedRole: changedRole,
          },
        }),
      );
      // Theme-compatible event for @app blocks / theme listeners.
      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.querySelector(".js-product-form"),
            variant: variant
              ? {
                  id: variant.id,
                  available: variant.available,
                  options: variant.options,
                  featured_media: variant.featuredMediaId
                    ? { id: Number(variant.featuredMediaId) }
                    : null,
                }
              : null,
            selectedOptions,
          },
        }),
      );
      this._log("variant change", {
        changedRole,
        selection: this.selection,
        variant,
      });
    }
  }

  if (!customElements.get("flagship-product")) {
    customElements.define("flagship-product", FlagshipProduct);
  }

  // Shared selection accessor — kept as THE contract for every consumer since
  // C1; the internals now just read the engine.
  function getFlagshipSelection(section) {
    return section && section.selection
      ? section.selection
      : { color: null, gender: null, size: null };
  }

  // ── A. Colour switching: hero crossfade + carousel filter ────────────────
  //
  // The hero <img> is a stable element we own (nothing replaces it), so the
  // crossfade is: clone the current img as an absolutely positioned "ghost"
  // pinned to .flagship-hero, retarget the live img at the new colour's image
  // (held at opacity 0 by .is-hero-crossfading), and — once the new image is
  // DECODED — fade ghost 1→0 and img 0→1 on the same frame. True
  // cross-dissolve; transparent-PNG shadows never stack (plan Appendix A).
  //
  // Both fades read their geometry on `flagship:variant:beforechange`, before
  // the engine's `data-color` write restyles the page; after the write they
  // only write, and what must be read after the swap waits for the next
  // frame, where the browser lays the page out once anyway.

  function initColorSwitching(engine) {
    engine._heroGhost = null;
    engine._heroGhostTimer = null;
    engine._galleryGhost = null;
    engine._galleryGhostTimer = null;
    let geometry = null;

    engine.addEventListener(
      "flagship:variant:beforechange",
      (evt) => {
        if (evt.detail.role === "color") geometry = measureColorSwitch(engine);
      },
      { signal: engine._ac.signal },
    );
    engine.addEventListener(
      "flagship:variant:change",
      (evt) => {
        if (evt.detail.changedRole !== "color") return;
        const color = evt.detail.selection.color;
        const geo = geometry || measureColorSwitch(engine);
        geometry = null;
        heroCrossfade(engine, color, geo.hero);
        galleryCrossfade(engine, color, geo.gallery);
      },
      { signal: engine._ac.signal },
    );
  }

  // The hero image's box inside .flagship-hero (where its ghost is pinned),
  // and whether the carousel is on screen and how far it is scrolled.
  function measureColorSwitch(engine) {
    const geo = { hero: null, gallery: null };
    const wrapper = engine.querySelector(".flagship-hero");
    const img = engine.querySelector(
      ".flagship-hero-media img:not(.flagship-hero-ghost)",
    );
    if (wrapper && img) {
      const imgRect = img.getBoundingClientRect();
      const wrapRect = wrapper.getBoundingClientRect();
      geo.hero = {
        top: imgRect.top - wrapRect.top,
        left: imgRect.left - wrapRect.left,
        width: imgRect.width,
        height: imgRect.height,
      };
    }
    const scroll = engine.querySelector(".carousel-scroll");
    const stage = scroll && scroll.closest(".carousel-stage");
    if (stage) {
      const rect = stage.getBoundingClientRect();
      geo.gallery = {
        inView: rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight,
        scrollLeft: scroll.scrollLeft,
      };
    }
    return geo;
  }

  // The carousel's colour change, as one dissolve. A colour's slides are a
  // different SET, so the swap itself is discrete; what carries it is a
  // snapshot of the outgoing track, pinned over the live one while the new
  // set is filtered in and scrolled home underneath, then faded out (460ms,
  // CSS). The track itself never changes opacity, so there is opaque content
  // over the carousel at every instant and nothing moves.
  //
  // What the snapshot has to be given time for is the picture UNDERNEATH it.
  // The incoming colour's slides were display:none, so their loading="lazy"
  // images never started a request; un-hiding them and fading the snapshot off
  // on the next frame revealed the slides' own white plate for as long as the
  // network took. That is the flash. So the slides the eye will land on are
  // made eager and the fade is armed only once they can paint — the same
  // prime-then-show the hero crossfade and the stack machine run on. The wait
  // is bounded: past the deadline the dissolve runs anyway, because holding
  // the colour the shopper just left on screen is the worse failure.
  const GALLERY_PRIME_MS = 900;

  function decodeWhenReady(img) {
    const decode = () =>
      typeof img.decode === "function"
        ? img.decode().catch(() => {})
        : Promise.resolve();
    if (img.complete && img.naturalWidth > 0) return decode();
    return new Promise((resolve) => {
      img.addEventListener("load", () => decode().then(resolve), {
        once: true,
      });
      img.addEventListener("error", resolve, { once: true });
    });
  }

  // Only the slides inside the track's own viewport: those are what the
  // dissolve uncovers, and waiting on the whole set would hold the snapshot
  // for the tail of an 18-image colourway nobody is looking at yet.
  function primeVisibleSlides(engine) {
    const scroll = engine.querySelector(".carousel-scroll");
    if (!scroll) return Promise.resolve();
    const edge = scroll.getBoundingClientRect().right;
    const jobs = [];
    for (const slide of getVisibleSlides(engine)) {
      if (slide.getBoundingClientRect().left >= edge) break;
      const img = slide.querySelector("img");
      // A video slide carries its own poster and nothing to prime.
      if (!img) continue;
      img.loading = "eager";
      jobs.push(decodeWhenReady(img));
    }
    return Promise.all(jobs);
  }

  function clearGalleryGhost(engine) {
    clearTimeout(engine._galleryGhostTimer);
    engine._galleryGhostTimer = null;
    if (engine._galleryGhost) {
      engine._galleryGhost.remove();
      engine._galleryGhost = null;
    }
  }

  // `geo` is the carousel's measure from before the colour write. The track
  // goes back home on the next frame: a scroll write here would force the
  // page's restyle inside the click.
  function galleryCrossfade(engine, color, geo) {
    if (!geo) {
      filterGalleryByColor(engine, color);
      return;
    }
    const scroll = engine.querySelector(".carousel-scroll");
    clearGalleryGhost(engine);

    // Off screen a plain swap is invisible: no snapshot to build.
    if (!geo.inView) {
      filterGalleryByColor(engine, color, { scrollHome: false });
      if (geo.scrollLeft) {
        requestAnimationFrame(() =>
          scroll.scrollTo({ left: 0, behavior: "instant" }),
        );
      }
      return;
    }

    const stage = scroll.closest(".carousel-stage");
    const ghost = document.createElement("div");
    ghost.className = "carousel-ghost";
    ghost.setAttribute("aria-hidden", "true");
    const track = scroll.cloneNode(true);
    track.removeAttribute("id");
    track.removeAttribute("role");
    track.removeAttribute("aria-label");
    // The snapshot is a picture, not a carousel: its slides give up the
    // `carousel-slide` hook so the filter, the dot rebuild and the lightbox
    // keep seeing exactly the live track's slides while the ghost is alive.
    // The two rules that hook name carries (the hidden state and the bleed
    // width) name the ghost's class too, in flagship-carousel.liquid.
    track.querySelectorAll(".carousel-slide").forEach((slide) => {
      slide.classList.replace("carousel-slide", "carousel-ghost-slide");
      slide.removeAttribute("data-fs-lb-open");
      slide.removeAttribute("tabindex");
      slide.removeAttribute("role");
    });
    // Every cloned image is pinned to the exact bytes its original is showing:
    // no srcset to re-select from, no lazy gate to pass, so the snapshot comes
    // off the memory cache rather than starting a second request that could
    // land at a different width (docs/lab-to-template.md rule 12). An original
    // that never loaded has no currentSrc and stays as it is — it is a picture
    // of a slide that is empty, which is what that slide looks like.
    const liveImages = scroll.querySelectorAll("img");
    track.querySelectorAll("img").forEach((img, i) => {
      const live = liveImages[i];
      img.removeAttribute("loading");
      img.removeAttribute("fetchpriority");
      if (live && live.currentSrc) {
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
        img.src = live.currentSrc;
      }
    });
    // A cloned <video> selects no resource and paints nothing in WebKit
    // (docs/gotchas.md), so each one becomes the poster frame it was already
    // showing — which is all a snapshot needs to be.
    track.querySelectorAll("video").forEach((video) => {
      const poster = document.createElement("img");
      poster.className = video.className;
      poster.src = video.poster || "";
      poster.alt = "";
      video.replaceWith(poster);
    });
    ghost.appendChild(track);
    stage.appendChild(ghost);
    engine._galleryGhost = ghost;

    // The rebuild happens under the snapshot, so it can be instant.
    filterGalleryByColor(engine, color, { scrollHome: false });

    // Whichever finishes first: the incoming pictures, or the deadline.
    const armFade = () => {
      if (engine._galleryGhost !== ghost || !ghost.isConnected) return;
      requestAnimationFrame(() => {
        ghost.addEventListener(
          "transitionend",
          () => {
            if (engine._galleryGhost === ghost) clearGalleryGhost(engine);
            else ghost.remove();
          },
          { once: true },
        );
        ghost.style.opacity = "0";
      });
    };
    const deadline = new Promise((resolve) =>
      setTimeout(resolve, GALLERY_PRIME_MS),
    );
    // Scroll writes and the slide measure wait for the frame's own layout;
    // nothing paints before it, so the snapshot still opens where it was.
    requestAnimationFrame(() => {
      if (geo.scrollLeft) {
        track.scrollLeft = geo.scrollLeft;
        scroll.scrollTo({ left: 0, behavior: "instant" });
      }
      Promise.race([primeVisibleSlides(engine), deadline]).then(armFade);
    });

    // Safety net: never let a ghost stick around over the gallery. It has to
    // outlast the longest honest run — the full prime deadline plus the fade.
    engine._galleryGhostTimer = setTimeout(
      () => clearGalleryGhost(engine),
      GALLERY_PRIME_MS + 1600,
    );
  }

  function clearHeroGhost(engine) {
    if (engine._heroGhostTimer) {
      clearTimeout(engine._heroGhostTimer);
      engine._heroGhostTimer = null;
    }
    if (engine._heroGhost) {
      engine._heroGhost.remove();
      engine._heroGhost = null;
    }
    engine.classList.remove("is-hero-crossfading");
  }

  // `box` is the live image's box in .flagship-hero, measured before the
  // colour write (measureColorSwitch).
  function heroCrossfade(engine, color, box) {
    const media = engine.facts.heroMedia && engine.facts.heroMedia[color];
    const wrapper = engine.querySelector(".flagship-hero");
    const img = engine.querySelector(
      ".flagship-hero-media img:not(.flagship-hero-ghost)",
    );
    if (!media || !wrapper || !img || !box) return;
    if (img.src === media.src) return; // same image — nothing to fade

    clearHeroGhost(engine);

    // Pin a snapshot of the outgoing image over the live one. The ghost is a
    // sibling of the media container (child of .flagship-hero), so it can't be
    // disturbed by anything that happens to the live img.
    const ghost = img.cloneNode(true);
    ghost.classList.add("flagship-hero-ghost");
    ghost.removeAttribute("loading");
    ghost.removeAttribute("fetchpriority");
    ghost.style.top = box.top + "px";
    ghost.style.left = box.left + "px";
    ghost.style.width = box.width + "px";
    ghost.style.height = box.height + "px";
    ghost.style.opacity = "1";
    wrapper.appendChild(ghost);
    engine._heroGhost = ghost;

    // Hold the live img invisible from the frame its src changes.
    engine.classList.add("is-hero-crossfading");
    if (media.srcset) img.srcset = media.srcset;
    img.src = media.src;

    const startFade = () => {
      if (!ghost.isConnected) return;
      ghost.addEventListener(
        "transitionend",
        () => {
          if (engine._heroGhost === ghost) clearHeroGhost(engine);
          else ghost.remove();
        },
        { once: true },
      );
      // Kick both transitions on the same frame: ghost 1→0 (inline) and live
      // img 0→1 (CSS resumes when .is-hero-crossfading drops).
      requestAnimationFrame(() => {
        ghost.style.opacity = "0";
        engine.classList.remove("is-hero-crossfading");
      });
    };

    // Start only when the incoming image is decoded (Appendix A rule 4).
    if (typeof img.decode === "function") {
      img.decode().then(startFade, () => startFade());
    } else if (img.complete && img.naturalWidth > 0) {
      requestAnimationFrame(() => requestAnimationFrame(startFade));
    } else {
      img.addEventListener("load", startFade, { once: true });
      img.addEventListener("error", () => clearHeroGhost(engine), {
        once: true,
      });
    }
    // Safety net: never let a ghost stick around.
    engine._heroGhostTimer = setTimeout(() => clearHeroGhost(engine), 2500);
  }

  // ── B. Secondary CTAs (hero + closing) — two-state mirror of the main ATC ─
  //
  // The hero "Select Size" button and the closing "Choose Your Size" button
  // mirror the sticky bar's two-state pattern:
  //   • no size yet → browse label, click routes to selection
  //     (desktop: scroll to the size panel; mobile: open the drawer)
  //   • size chosen → "Add to Cart", click submits the main form
  // Readiness comes straight from the engine.

  // Route the shopper to where a size can be picked: the drawer on mobile, a
  // smooth scroll to the size controls on desktop. "Mobile" is the drawer's
  // range: below 768 on the classic layout, below the story layout's 760
  // seam (at 760+ the Frame hero owns the buying and the drawer never opens),
  // below 1024 on the standard page, whose sizes stand on the page from 1024.
  function flagshipBrowseToSelection(engine, trigger) {
    const sectionId = engine.dataset.sectionId;
    const isStory = engine.dataset.layout === "story";
    const isStandard = engine.dataset.layout === "standard";
    // The showroom: wide screens pick the size on the page, under the
    // colours ([data-fs-size-field], shown from 1024), so the button nudges
    // those squares and focuses the first one; phones use the drawer.
    if (engine.dataset.layout === "showroom") {
      const field = engine.querySelector("[data-fs-size-field]");
      if (field && field.offsetParent !== null) {
        field.classList.remove("is-nudged");
        void field.offsetWidth; // reflow so the shake can replay
        field.classList.add("is-nudged");
        const first = field.querySelector(
          ".sr-sizes .js-option:not(.is-unavailable)",
        );
        if (first) first.focus({ preventScroll: true });
        return;
      }
      const drawer = document.getElementById(
        `flagship-size-drawer-${sectionId}`,
      );
      if (drawer) drawer.open(trigger);
      return;
    }
    let mobileQuery = "(max-width: 767px)";
    if (isStory) mobileQuery = "(max-width: 759.98px)";
    if (isStandard) mobileQuery = "(max-width: 1023.98px)";
    const isMobile = window.matchMedia(mobileQuery).matches;
    if (isMobile) {
      const drawer = document.getElementById(
        `flagship-size-drawer-${sectionId}`,
      );
      if (drawer) drawer.open(trigger);
      return;
    }
    if (isStandard) {
      const field = engine.querySelector("[data-fs-size-field]");
      if (field) field.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (isStory) {
      // The Frame hero's size grid, standing on the glass at the page's top.
      const grid =
        engine.querySelector(".fsf-grid") ||
        engine.querySelector("[data-fs-frame]");
      if (grid) grid.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const highlights = engine.querySelector(".flagship-highlights");
    if (!highlights) return;
    // Guard the theme globals we borrow (plan 1.4) — fall back to window
    // scrolling if the theme ever renames them.
    const scroller =
      window.theme && typeof theme.getScrollParent === "function"
        ? theme.getScrollParent(highlights)
        : window;
    const stickyHeader =
      (window.theme &&
        typeof theme.stickyHeaderHeight === "function" &&
        theme.stickyHeaderHeight()) ||
      0;
    // Negative: highlights bg matches hero bg, so the visible "start" is the
    // inner heading — scroll a bit past the wrapper top so the heading lands
    // just under the sticky header.
    const EXTRA_OFFSET = -40;
    const scrollTop = scroller === window ? window.scrollY : scroller.scrollTop;
    const top =
      highlights.getBoundingClientRect().top +
      scrollTop -
      stickyHeader -
      EXTRA_OFFSET;
    scroller.scrollTo({ top, left: 0, behavior: "smooth" });
  }

  function bindSecondaryCta(engine, btn) {
    // The server-rendered label is the "browse" copy ("Select Size" /
    // "Choose Your Size"); remember it to restore when no size is set.
    const browseLabel = (
      btn.dataset.browseLabel ||
      btn.textContent ||
      "Select Size"
    ).trim();

    const update = () => {
      if (engine.isReady()) {
        btn.textContent = "Add to Cart";
        btn.dataset.state = "add";
      } else {
        btn.textContent = browseLabel;
        btn.dataset.state = "select";
      }
    };

    btn.addEventListener(
      "click",
      () => {
        if (flagshipNoClick && Date.now() - flagshipNoClick < 350) return;
        if (btn.dataset.state === "add") {
          submitMainForm(engine);
        } else {
          flagshipBrowseToSelection(engine, btn);
        }
      },
      { signal: engine._ac.signal },
    );

    engine.addEventListener("flagship:variant:change", update, {
      signal: engine._ac.signal,
    });
    update();
  }

  function bindClosingCtas(engine) {
    // The closing CTA lives in a separate section above the footer, outside
    // this element. Guard by-engine so an editor re-render of EITHER section
    // rebinds cleanly (stale listeners die with the engine's AbortController).
    document.querySelectorAll("[data-flagship-cta-top]").forEach((btn) => {
      if (btn._fsBoundEngine === engine) return;
      btn._fsBoundEngine = engine;
      bindSecondaryCta(engine, btn);
    });
  }

  function initSecondaryCtas(engine) {
    const heroBtn = engine.querySelector("[data-flagship-select-size]");
    if (heroBtn) bindSecondaryCta(engine, heroBtn);

    bindClosingCtas(engine);
    // Editor glue (plan 0.6): if the closing section is re-rendered, its
    // buttons are fresh nodes — rebind them.
    document.addEventListener(
      "shopify:section:load",
      () => bindClosingCtas(engine),
      { signal: engine._ac.signal },
    );
  }

  // ── C. Sticky bar ─────────────────────────────────────────────────────────

  function initStickyBar(engine) {
    const sectionId = engine.dataset.sectionId;
    const stickyBar = document.getElementById(`flagship-sticky-${sectionId}`);
    if (!stickyBar) return;

    const openDrawer = (trigger) => {
      const drawer = document.getElementById(
        `flagship-size-drawer-${sectionId}`,
      );
      if (drawer) drawer.open(trigger);
    };

    // Click: a READY CTA (size chosen) adds to cart; otherwise — and for any
    // other tap on the bar (readout, hill) — open the drawer.
    stickyBar.addEventListener(
      "click",
      (evt) => {
        if (flagshipNoClick && Date.now() - flagshipNoClick < 350) return;
        const cta = evt.target.closest("[data-flagship-sticky-cta]");
        if (cta && cta.dataset.state === "add") {
          submitMainForm(engine);
          return;
        }
        openDrawer(cta || stickyBar);
      },
      { signal: engine._ac.signal },
    );

    engine.addEventListener(
      "flagship:variant:change",
      () => updateStickyBar(engine, stickyBar),
      { signal: engine._ac.signal },
    );

    // Initial sync: a deep-linked size lands in the "Add to Cart" ready state.
    updateStickyBar(engine, stickyBar);
  }

  // Drive the 10b two-state bar from the engine. Fills the readout (swatch +
  // "Size X" + colour name) and flips browse ↔ ready: the CTA morphs
  // "Select your size" → "Add to Cart · <price>", the readout reveals, and the
  // hill affordance appears. ATC state is a pure function of the variant.
  function updateStickyBar(engine, stickyBar) {
    const { color, size } = getFlagshipSelection(engine);

    const colorEl = stickyBar.querySelector("[data-flagship-sticky-color]");
    if (colorEl && color) colorEl.textContent = color;

    // Swatch fill is CSS ([data-swatch] rules in application.css), or, where
    // the page carries no palette (the standard page), a photograph of the
    // colour from the engine data's swatches map.
    const swatchEl = stickyBar.querySelector("[data-flagship-sticky-swatch]");
    if (swatchEl && color) {
      swatchEl.dataset.swatch = handleize(color);
      const photo = engine.facts.swatches && engine.facts.swatches[color];
      if (photo) swatchEl.style.backgroundImage = `url("${photo}")`;
    }

    const sizeLabelEl = stickyBar.querySelector(
      "[data-flagship-sticky-sizelabel]",
    );
    if (sizeLabelEl) sizeLabelEl.textContent = size ? "Size " + size : "Size";

    const ready = engine.isReady();
    stickyBar.classList.toggle("is-ready", ready);

    const cta = stickyBar.querySelector("[data-flagship-sticky-cta]");
    if (cta) {
      if (ready) {
        // Liquid-money price string from the engine data (localised per store).
        const price = engine.variant && engine.variant.price;
        cta.textContent = price ? "Add to Cart · " + price : "Add to Cart";
        cta.dataset.state = "add";
      } else {
        cta.textContent = cta.dataset.empty || "Select your size";
        cta.dataset.state = "select";
      }
    }
  }

  // ── D. Size drawer ────────────────────────────────────────────────────────

  // Custom slide-up drawer element (concept 10b). Mirrors the theme drawer's
  // open(opener)/close() contract, so the sticky bar and hero CTA callers are
  // unchanged. Spring open/close is CSS (.is-open). While open, Tab cycles
  // within the sheet via the theme's trapFocus (main.js) and aria-modal
  // covers AT; close() releases the trap and refocuses the opener (3.A).
  // Nothing outside the drawer is made inert and focus is never yanked back,
  // so late third-party overlays (Klaviyo popup) stay fully interactive.
  class FlagshipDrawer extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      this.addEventListener(
        "click",
        (evt) => {
          if (evt.target.closest("[data-flagship-drawer-close]")) this.close();
        },
        { signal: this._ac.signal },
      );
      document.addEventListener(
        "keydown",
        (evt) => {
          if (evt.key === "Escape" && this._isOpen) this.close();
        },
        { signal: this._ac.signal },
      );
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._isOpen) {
        document.documentElement.classList.remove("flagship-drawer-lock");
        window.removeTrapFocus();
      }
    }

    open(opener) {
      if (this._isOpen) return;
      this._isOpen = true;
      this._opener = opener && opener.focus ? opener : null;
      this.classList.add("is-open");
      this.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("flagship-drawer-lock");
      const sheet = this.querySelector(".flagship-drawer__sheet");
      if (sheet && !sheet.hasAttribute("tabindex")) {
        sheet.setAttribute("tabindex", "-1");
      }
      window.trapFocus(this, sheet || this);
      requestAnimationFrame(() => placeDrawerSegIndicators(this));
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;
      this.classList.remove("is-open");
      this.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("flagship-drawer-lock");
      window.removeTrapFocus(this._opener);
    }
  }
  if (!customElements.get("flagship-drawer")) {
    customElements.define("flagship-drawer", FlagshipDrawer);
  }

  // Position each segmented control's sliding indicator under its selection.
  function placeDrawerSegIndicators(drawer) {
    drawer.querySelectorAll(".flagship-drawer-seg").forEach((seg) => {
      const ind = seg.querySelector(".flagship-drawer-seg__ind");
      const sel =
        seg.querySelector(".flagship-drawer-seg__btn.is-selected") ||
        seg.querySelector(".flagship-drawer-seg__btn");
      if (!ind || !sel || !sel.offsetWidth) return;
      ind.style.width = sel.offsetWidth + "px";
      ind.style.transform = "translateX(" + (sel.offsetLeft - 4) + "px)";
    });
  }

  // Keep the drawer summary (thumbnail photo + price) in sync.
  function updateDrawerSummary(engine, drawer) {
    // Price pair tracks the resolved variant (Liquid-money strings from the
    // engine data; compareAt is null unless it beats the price, which hides
    // the strikethrough). While no variant is resolved the last shown pair —
    // initially the Liquid-rendered product price — stands.
    const variant = engine.variant;
    if (variant && variant.price) {
      const priceEl = drawer.querySelector(
        "[data-flagship-drawer-summary-price]",
      );
      if (priceEl) priceEl.textContent = variant.price;
      const compareEl = drawer.querySelector(
        "[data-flagship-drawer-summary-compare]",
      );
      if (compareEl) {
        compareEl.textContent = variant.compareAt || "";
        compareEl.hidden = !variant.compareAt;
      }
      const dealEl = drawer.querySelector(
        "[data-flagship-drawer-summary-deal]",
      );
      if (dealEl) dealEl.hidden = !variant.compareAt;
    }

    const { color } = getFlagshipSelection(engine);
    if (!color) return;
    // Point the thumbnail at the selected colour's hero photo. Same URLs the
    // hero uses (preloaded per colour), so the swap is an instant cache hit.
    const thumb = drawer.querySelector("[data-flagship-drawer-summary-thumb]");
    if (thumb && thumb.tagName === "IMG") {
      const media = engine.facts.heroMedia && engine.facts.heroMedia[color];
      if (media) {
        if (media.srcset) thumb.srcset = media.srcset;
        if (media.src) thumb.src = media.src;
      }
    }
  }

  // Shake the size grid when the ATC is tapped before a size is chosen.
  function flagshipNudge(drawer) {
    const grid = drawer.querySelector(".flagship-drawer__grid");
    if (!grid) return;
    grid.classList.remove("flagship-drawer__shake");
    void grid.offsetWidth; // reflow so the animation can replay
    grid.classList.add("flagship-drawer__shake");
  }

  // In-button success morph on a CONFIRMED cart add (driven by the theme's
  // on:cart:add — so it only fires when the add actually succeeded).
  function flagshipAddedSuccess(engine) {
    const sectionId = engine.dataset.sectionId;
    const drawer = document.getElementById(`flagship-size-drawer-${sectionId}`);
    const ctas = [];
    const sticky = document.querySelector(
      `#flagship-sticky-${sectionId} [data-flagship-sticky-cta]`,
    );
    if (sticky && sticky.dataset.state === "add") ctas.push(sticky);
    const drawerAtc =
      drawer && drawer.querySelector("[data-flagship-drawer-atc]");
    if (drawerAtc) ctas.push(drawerAtc);
    if (!ctas.length) return;

    ctas.forEach((b) => {
      b.dataset.prevLabel = b.textContent;
      b.textContent = "Added to bag ✓";
      b.classList.add("is-added");
    });
    // Announce the confirmed add for screen readers too.
    const live = engine.querySelector("[data-flagship-announce]");
    if (live) live.textContent = "Added to bag";
    if (drawer && typeof drawer.close === "function") {
      setTimeout(() => drawer.close(), 700);
    }
    setTimeout(() => {
      ctas.forEach((b) => {
        b.classList.remove("is-added");
        if (b.dataset.prevLabel) {
          b.textContent = b.dataset.prevLabel;
          delete b.dataset.prevLabel;
        }
      });
    }, 1500);
  }

  function initSizeDrawer(engine) {
    const sectionId = engine.dataset.sectionId;
    const drawer = document.getElementById(`flagship-size-drawer-${sectionId}`);
    if (!drawer) return;
    const { signal } = engine._ac;

    // Drawer option button → write to the engine (optimistic class flip for
    // instant feedback; the engine's event re-syncs everything consistently).
    drawer.addEventListener(
      "click",
      (evt) => {
        if (flagshipNoClick && Date.now() - flagshipNoClick < 350) return;
        const btn = evt.target.closest("[data-drawer-value]");
        if (!btn || btn.disabled) return;

        const btnGroup = btn.closest("[data-drawer-option]");
        const optionName = btnGroup && btnGroup.dataset.drawerOption;
        const value = btn.dataset.drawerValue;
        if (!optionName || !value) return;

        btnGroup
          .querySelectorAll("[data-drawer-value]")
          .forEach((b) => b.classList.remove("is-selected"));
        btn.classList.add("is-selected");
        placeDrawerSegIndicators(drawer);

        engine.setOptionByName(optionName, value);
      },
      { signal },
    );

    // Drawer ATC → submit + close when ready; nudge the size grid when not.
    drawer.addEventListener(
      "click",
      (evt) => {
        if (flagshipNoClick && Date.now() - flagshipNoClick < 350) return;
        const btn = evt.target.closest("[data-flagship-drawer-atc]");
        if (!btn) return;
        if (btn.getAttribute("aria-disabled") === "true") {
          flagshipNudge(drawer);
          return;
        }
        submitMainForm(engine);
        drawer.close();
      },
      { signal },
    );

    // Sync drawer state when the variant changes.
    engine.addEventListener(
      "flagship:variant:change",
      (evt) => {
        syncDrawerToEngine(engine, drawer);
        // A colour never moves the fit indicator, and measuring it here
        // would force the page's colour restyle inside the click.
        if (evt.detail.changedRole !== "color") {
          placeDrawerSegIndicators(drawer);
        }
        updateDrawerSummary(engine, drawer);
      },
      { signal },
    );

    // Success morph on a confirmed cart add (decoupled from the add itself).
    engine.addEventListener("on:cart:add", () => flagshipAddedSuccess(engine), {
      signal,
    });

    // Initial sync — reflect the current selection before the first open.
    syncDrawerToEngine(engine, drawer);
    placeDrawerSegIndicators(drawer);
    updateDrawerSummary(engine, drawer);
  }

  function syncDrawerToEngine(engine, drawer) {
    // Selection + availability straight from the engine (funnel-filtered).
    drawer.querySelectorAll("[data-drawer-option]").forEach((group) => {
      const name = group.dataset.drawerOption;
      const role = engine._roleForName(name);
      if (!role) return;
      const selVal = engine.selection[role] ?? null;
      const avail = engine.availableValues(role);

      group.querySelectorAll("[data-drawer-value]").forEach((btn) => {
        const value = btn.dataset.drawerValue;
        btn.classList.toggle("is-selected", value === selVal);
        const isUnavailable = !avail.has(value);
        btn.classList.toggle("is-unavailable", isUnavailable);
        btn.disabled = isUnavailable;
      });
    });

    // Drawer ATC mirrors the main ATC state.
    const atc = drawer.querySelector("[data-flagship-drawer-atc]");
    if (atc) {
      const variant = engine.variant;
      if (variant && !variant.available) {
        atc.textContent = atc.dataset.soldoutLabel || "Sold out";
        atc.setAttribute("aria-disabled", "true");
      } else {
        atc.textContent = atc.dataset.addLabel || "Add to cart";
        if (engine.isReady()) atc.removeAttribute("aria-disabled");
        else atc.setAttribute("aria-disabled", "true");
      }
    }
  }

  // ── Drag-to-open / flick-to-close (ported from lab concept 10b) ───────────
  // Drag UP on the sticky bar opens the drawer (follows the finger); flick/drag
  // DOWN on the open sheet closes it — decided by RELEASE position vs the
  // viewport midpoint + velocity, so reversing before release cancels. iOS
  // guards: touch events with preventDefault, no static will-change, content
  // forced visible while dragging, a 350ms synthetic-click guard, and window
  // move/end listeners bound only for the duration of a gesture.
  // NOTE: gesture FEEL must be verified on a real device — headless can't
  // validate touch; the logic is smoke-tested via mouse.
  function initDrawerDrag(engine) {
    const sectionId = engine.dataset.sectionId;
    const drawer = document.getElementById(`flagship-size-drawer-${sectionId}`);
    if (!drawer) return;
    const bar = document.getElementById(`flagship-sticky-${sectionId}`);
    const sheet = drawer.querySelector(".flagship-drawer__sheet");
    if (!sheet) return;
    const body = drawer.querySelector(".flagship-drawer__body");
    const scrim = drawer.querySelector(".flagship-drawer__scrim");
    const { signal } = engine._ac;

    let mode = 0; // 1 = drag-up-to-open (bar), 2 = drag-down-to-close (sheet)
    let startY = 0;
    let H = 0;
    let lastDy = 0;
    let vy = 0;
    let prevDy = 0;
    let prevT = 0;
    let capturing = false;
    let fromHandle = false;
    let opener = null;

    const Y = (e) => (e.touches ? e.touches[0].clientY : e.clientY);
    const T = (e) => e.timeStamp || Date.now();
    const setScrim = (v) => {
      if (!scrim) return;
      scrim.style.transition = "none";
      scrim.style.opacity = String(Math.max(0, Math.min(1, v)));
    };
    const resetScrim = () => {
      if (scrim) {
        scrim.style.transition = "";
        scrim.style.opacity = "";
      }
    };

    function resetTrack(e) {
      startY = Y(e);
      lastDy = 0;
      vy = 0;
      prevDy = 0;
      prevT = T(e);
      capturing = false;
    }
    function track(e) {
      const dy = Y(e) - startY;
      const t = T(e);
      if (prevT) {
        const dt = t - prevT;
        if (dt > 0) vy = (dy - prevDy) / dt;
      }
      prevDy = dy;
      prevT = t;
      lastDy = dy;
      return dy;
    }
    function begin() {
      capturing = true;
      drawer.classList.add("is-dragging");
    }
    function move(e) {
      if (!mode) return;
      const dy = track(e);
      if (mode === 1) {
        if (!capturing) {
          if (dy < -5) begin();
          else if (dy > 6) {
            mode = 0;
            return;
          } else return;
        }
        if (e.cancelable) e.preventDefault();
        const up = Math.max(0, Math.min(H, -dy));
        sheet.style.transform = "translateY(" + (H - up) + "px)";
        setScrim(up / H);
      } else {
        if (!capturing) {
          if (dy > 5 && (fromHandle || (body && body.scrollTop <= 0))) begin();
          else if (dy < -3 || (body && body.scrollTop > 0)) {
            mode = 0;
            return;
          } else return;
        }
        if (e.cancelable) e.preventDefault();
        const dn = Math.max(0, Math.min(H, dy));
        sheet.style.transform = "translateY(" + dn + "px)";
        setScrim(1 - dn / H);
      }
    }
    function wantsClose() {
      if (vy < -0.35) return false;
      if (vy > 0.35) return true;
      const r = sheet.getBoundingClientRect();
      const pr = drawer.getBoundingClientRect();
      return r.top - pr.top > pr.height * 0.5;
    }
    function settle(open) {
      drawer.classList.remove("is-dragging");
      void sheet.offsetHeight; // commit the dragged transform as the start frame
      if (open) drawer.open(opener);
      else drawer.close();
      sheet.style.transform = "";
    }
    function end() {
      if (!mode) return;
      const m = mode;
      const wasCap = capturing;
      mode = 0;
      capturing = false;
      unbindGesture();
      resetScrim();
      if (!wasCap) {
        drawer.classList.remove("is-dragging");
        return; // a tap → let the click handlers decide
      }
      flagshipNoClick = Date.now(); // suppress the post-drag synthetic click
      if (m === 1) settle(-lastDy > H * 0.18 || vy < -0.5);
      else settle(!wantsClose());
    }

    function bindGesture() {
      window.addEventListener("touchmove", move, { passive: false, signal });
      window.addEventListener("mousemove", move, { signal });
      window.addEventListener("touchend", end, { signal });
      window.addEventListener("mouseup", end, { signal });
      window.addEventListener("touchcancel", end, { signal });
    }
    function unbindGesture() {
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchend", end);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchcancel", end);
    }
    function startBar(e) {
      if (e.button > 0 || drawer.classList.contains("is-open")) return;
      mode = 1;
      opener = bar;
      resetTrack(e);
      H = sheet.offsetHeight || 480;
      bindGesture();
    }
    function startSheet(e) {
      if (e.button > 0 || !drawer.classList.contains("is-open")) return;
      mode = 2;
      resetTrack(e);
      H = sheet.offsetHeight || 480;
      fromHandle = !!(
        e.target.closest && e.target.closest(".flagship-drawer__handle")
      );
      bindGesture();
    }

    if (bar) {
      bar.addEventListener("touchstart", startBar, { passive: true, signal });
      bar.addEventListener("mousedown", startBar, { signal });
    }
    sheet.addEventListener("touchstart", startSheet, {
      passive: true,
      signal,
    });
    sheet.addEventListener("mousedown", startSheet, { signal });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  // The variant whose money a price shows while no size is resolved: the
  // first variant of the chosen colour + fit (available first), so the money
  // always belongs to the photograph on the wall; the exact variant takes
  // over the moment one resolves. Its price and compareAt are Liquid money
  // strings from the engine's data, never formatted here.
  function repVariant(engine) {
    if (engine.variant) return engine.variant;
    const facts = engine.facts;
    const sel = engine.selection;
    const ci = facts.byRole.color;
    const gi = facts.byRole.gender;
    const match = (v) =>
      (ci == null || !sel.color || v.options[ci] === sel.color) &&
      (gi == null || !sel.gender || v.options[gi] === sel.gender);
    return (
      facts.variants.find((v) => v.available && match(v)) ||
      facts.variants.find(match) ||
      null
    );
  }

  // The add-to-cart request is the flagship's own (since 2026-09-08). Every
  // control (drawer, sticky bar, Frame ATC, the buy-box button's own submit)
  // lands here, never in the vendor <product-form>: that path opens the
  // theme's cart drawer after a successful add and throws where a store has
  // that drawer off (UK and CA run cart_type "page"), which left the add
  // button disabled for the rest of the visit and dropped every later add of
  // the same size without a request. Owning the request also puts
  // on:cart:add back on every store: UpCart wraps fetch to open its drawer on
  // /cart/add and never re-dispatches the theme event, so the "Added to bag"
  // morphs and the header count only ever fire from here (docs/gotchas.md).
  // The button is disabled for the flight and restored whatever happens.
  function submitMainForm(engine) {
    const form = engine.querySelector(".js-product-form");
    const addBtn = engine.querySelector('[name="add"]');
    if (!form || engine._adding) return;
    if (
      addBtn &&
      (addBtn.disabled || addBtn.getAttribute("aria-disabled") === "true")
    ) {
      return;
    }
    const routes = (window.theme && window.theme.routes) || {};
    // The showroom is a sales page: a shopper adds and keeps browsing, so
    // its adds tell UpCart not to open its drawer. UpCart reads these two
    // parameters off any /cart/add request it intercepts (its own adds send
    // them); "never" still refreshes its cart and count (docs/gotchas.md).
    const addUrl =
      (routes.cartAdd || "/cart/add.js") +
      (engine.dataset.layout === "showroom"
        ? "?upcart=1&opens_cart=never"
        : "");
    const cartUrl = (routes.cart || "/cart") + ".js";
    const headers = {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    };
    const live = engine.querySelector("[data-flagship-announce]");

    engine._adding = true;
    if (addBtn) addBtn.setAttribute("aria-disabled", "true");
    (async () => {
      try {
        const response = await fetch(addUrl, {
          method: "POST",
          headers,
          body: new FormData(form),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            data.description || data.message || String(response.status),
          );
        }
        // The count the header shows comes from the cart itself, not a +1.
        const cart = await fetch(cartUrl, { headers })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
        engine.dispatchEvent(
          new CustomEvent("on:cart:add", {
            bubbles: true,
            detail: { variantId: data.variant_id, cart },
          }),
        );
      } catch (error) {
        if (live) live.textContent = error.message;
        engine.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: { error: error.message },
          }),
        );
      } finally {
        engine._adding = false;
        if (addBtn) {
          if (engine.isReady()) addBtn.removeAttribute("aria-disabled");
          else addBtn.setAttribute("aria-disabled", "true");
        }
      }
    })();
  }

  // ── E. Gallery colour filtering ───────────────────────────────────────────

  // `scrollHome: false` leaves the track's return to the first picture to the
  // caller (a colour pick does it on the next frame, see galleryCrossfade).
  function filterGalleryByColor(engine, color, { scrollHome = true } = {}) {
    if (!color) return;
    const isClassicLayout = engine.dataset.layout === "classic";
    // Hide the selected colour's hero render (it IS the hero image — locked
    // decision 8); the carousel starts from the next media.
    const heroMedia = engine.facts.heroMedia && engine.facts.heroMedia[color];
    const featuredId =
      (heroMedia && heroMedia.mediaId) ||
      (engine.variant && engine.variant.featuredMediaId) ||
      engine.dataset.featuredMediaId ||
      null;

    engine.querySelectorAll(".carousel-slide").forEach((slide) => {
      const slideColor = slide.dataset.slideColor;
      const mediaId = slide.dataset.mediaId;
      // "*" marks an all-groups image (show for every color).
      const colorMatches =
        !slideColor || slideColor === "*" || slideColor === color;
      // Classic layout only: the hero renders that image above the carousel,
      // so showing it again would duplicate it. The story and standard layouts
      // have no hero render; their carousel opens ON the colour's featured image.
      const isHeroImage =
        isClassicLayout && featuredId && mediaId === featuredId;
      slide.classList.toggle("is-hidden", !colorMatches || isHeroImage);
    });

    const scroll = engine.querySelector(".carousel-scroll");
    if (scroll && scrollHome) scroll.scrollTo({ left: 0, behavior: "instant" });

    // The track is home, so the current picture is the first one; reading it
    // back would force a layout.
    rebuildCarouselDots(engine, 0);
  }

  // ── F. Carousel navigation ────────────────────────────────────────────────

  function getVisibleSlides(engine) {
    return Array.from(
      engine.querySelectorAll(".carousel-slide:not(.is-hidden)"),
    );
  }

  function rebuildCarouselDots(engine, index) {
    const dotsContainer = engine.querySelector("[data-carousel-dots]");
    if (!dotsContainer) return;

    const slides = getVisibleSlides(engine);
    dotsContainer.innerHTML = slides
      .map(
        (_, i) =>
          `<button class="carousel-dot${i === 0 ? " is-active" : ""}" data-dot-index="${i}" aria-label="Go to media ${i + 1}"></button>`,
      )
      .join("");

    dotsContainer.querySelectorAll(".carousel-dot").forEach((dot) => {
      dot.addEventListener("click", () => {
        const idx = parseInt(dot.dataset.dotIndex, 10);
        scrollToSlideIndex(engine, idx);
      });
    });

    updateCarouselButtons(engine, index);
  }

  // `behavior` is "instant" when the lightbox lands the carousel on the image
  // it was closed from: the FLIP needs the destination rectangle to be final.
  function scrollToSlideIndex(engine, index, behavior = "smooth") {
    const slides = getVisibleSlides(engine);
    if (!slides[index]) return;

    const scroll = engine.querySelector(".carousel-scroll");
    if (!scroll) return;

    const slideLeft = slides[index].offsetLeft - scroll.offsetLeft;
    scroll.scrollTo({ left: slideLeft, behavior });
  }

  function getCurrentSlideIndex(engine) {
    const scroll = engine.querySelector(".carousel-scroll");
    if (!scroll) return 0;

    const slides = getVisibleSlides(engine);
    if (!slides.length) return 0;

    const scrollLeft = scroll.scrollLeft;
    let closest = 0;
    let minDist = Infinity;

    slides.forEach((slide, i) => {
      const dist = Math.abs(slide.offsetLeft - scroll.offsetLeft - scrollLeft);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    return closest;
  }

  function updateCarouselDots(engine) {
    const idx = getCurrentSlideIndex(engine);
    engine.querySelectorAll(".carousel-dot").forEach((dot, i) => {
      dot.classList.toggle("is-active", i === idx);
    });
    updateCarouselButtons(engine);
  }

  function updateCarouselButtons(engine, idx = getCurrentSlideIndex(engine)) {
    const slides = getVisibleSlides(engine);

    const prevBtn = engine.querySelector("[data-carousel-prev]");
    const nextBtn = engine.querySelector("[data-carousel-next]");

    if (prevBtn) prevBtn.disabled = idx === 0;
    if (nextBtn) nextBtn.disabled = idx >= slides.length - 1;
  }

  function initGalleryCarousel(engine) {
    const scroll = engine.querySelector(".carousel-scroll");
    if (!scroll) return;

    const sectionId = engine.dataset.sectionId;
    const { signal } = engine._ac;

    engine.addEventListener(
      "click",
      (evt) => {
        const prevBtn = evt.target.closest(
          `[data-carousel-prev="${sectionId}"]`,
        );
        const nextBtn = evt.target.closest(
          `[data-carousel-next="${sectionId}"]`,
        );

        if (prevBtn) {
          const idx = getCurrentSlideIndex(engine);
          scrollToSlideIndex(engine, Math.max(0, idx - 1));
        }
        if (nextBtn) {
          const slides = getVisibleSlides(engine);
          const idx = getCurrentSlideIndex(engine);
          scrollToSlideIndex(engine, Math.min(slides.length - 1, idx + 1));
        }
      },
      { signal },
    );

    scroll.addEventListener("scrollend", () => updateCarouselDots(engine), {
      passive: true,
      signal,
    });
    // Fallback for browsers without scrollend
    scroll.addEventListener(
      "scroll",
      () => {
        clearTimeout(scroll._scrollTimer);
        scroll._scrollTimer = setTimeout(() => updateCarouselDots(engine), 120);
      },
      { passive: true, signal },
    );

    rebuildCarouselDots(engine);
  }

  // ── G. Media→colour map application ──────────────────────────────────────

  function applyMediaColorMap(engine) {
    const map = engine.facts.mediaColors;
    if (!map) return;
    engine
      .querySelectorAll(".carousel-slide[data-media-id]")
      .forEach((slide) => {
        const mediaId = slide.dataset.mediaId;
        if (mediaId && map[mediaId]) {
          slide.dataset.slideColor = map[mediaId];
        }
      });
  }

  // ── H. Gallery video playback ─────────────────────────────────────────────
  //
  // We own the carousel <video> markup (plain video_tag output: mp4 + auto
  // poster, muted/playsinline/loop, preload="none", NO autoplay attribute).
  // Playback is JS-driven: a video plays only while its slide is actually on
  // screen and pauses the moment it leaves — nothing downloads or plays at
  // page load, so the hero LCP has the network to itself. Native controls are
  // never shown (2026-08-05 client call — the loop is ambient, muted product
  // footage): when the OS refuses programmatic play (iOS Low Power Mode,
  // Android battery/data saver, and previously reduced-motion), the poster
  // stays up and we retry on the first real user gesture, which lifts the
  // OS restriction.
  //
  // While the lightbox is up it plays its own copy of the video, so the
  // carousel's stay paused. Section M dispatches `fs-lightbox` on the engine
  // at both ends of that, and everything here reads the same open flag, so
  // neither the gesture retry nor a tab switch can restart one behind it.

  function initGalleryVideos(engine) {
    const videos = engine.querySelectorAll(".carousel-slide video");
    if (!videos.length) return;
    const { signal } = engine._ac;

    const blocked = new Set();
    const visible = new Set();

    // Blocked slides get a CSS play badge (styled in flagship-highlights)
    // so the poster doesn't read as a still photo — the user sees a tap
    // starts it.
    const setBlockedCue = (video, on) => {
      const slide = video.closest(".carousel-slide");
      if (slide) slide.classList.toggle("video-blocked", on);
    };

    const inLightbox = () => engine.classList.contains("is-lightbox-open");

    const tryPlay = (video) => {
      if (inLightbox()) return;
      // The muted PROPERTY (not just the attribute) is what iOS checks
      // before allowing a programmatic play.
      video.muted = true;
      const played = video.play();
      if (played) {
        played
          .then(() => {
            blocked.delete(video);
            setBlockedCue(video, false);
          })
          .catch(() => {
            blocked.add(video);
            setBlockedCue(video, true);
          });
      }
    };

    // Any real user gesture lifts an OS autoplay refusal — retry whichever
    // blocked videos are still on screen.
    const onGesture = () => {
      blocked.forEach((video) => {
        if (visible.has(video)) tryPlay(video);
      });
    };
    document.addEventListener("touchend", onGesture, { passive: true, signal });
    document.addEventListener("click", onGesture, { signal });

    // Browsers pause video-only media when the page is hidden (app/tab
    // switch) and do NOT resume on return; the observer never re-fires
    // because intersection never changed. Re-kick whatever is on screen.
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState !== "visible") return;
        visible.forEach((video) => {
          if (video.paused) tryPlay(video);
        });
      },
      { signal },
    );

    engine.addEventListener(
      "fs-lightbox",
      () => {
        if (inLightbox()) videos.forEach((video) => video.pause());
        else
          visible.forEach((video) => {
            if (video.paused) tryPlay(video);
          });
      },
      { signal },
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            visible.add(target);
            tryPlay(target);
          } else {
            visible.delete(target);
            if (!target.paused) target.pause();
          }
        });
      },
      { threshold: 0.4 },
    );

    videos.forEach((video) => observer.observe(video));
    signal.addEventListener("abort", () => observer.disconnect());
  }

  // ── I. Hero image warmup (9.2) ────────────────────────────────────────────
  //
  // The other colours' hero images are fetched AFTER load, at idle, instead of
  // via render-time <link rel=preload> (which competed with the LCP). The
  // crossfade still decode()-gates, so a cold cache only delays the fade, never
  // flashes.

  function warmHeroImages(engine) {
    const heroMedia = engine.facts.heroMedia;
    // The standard page's heroMedia only feeds the drawer's thumbnail, and
    // its gallery primes a colour's pictures on the change itself; warming
    // every colour at idle would be weight for a picture nobody asked for.
    if (
      !heroMedia ||
      engine.dataset.layout === "standard" ||
      engine.dataset.layout === "showroom"
    )
      return;
    const img = engine.querySelector(
      ".flagship-hero-media img:not(.flagship-hero-ghost)",
    );

    const warm = () => {
      if (engine._ac.signal.aborted) return;
      Object.keys(heroMedia).forEach((color) => {
        if (color === engine.selection.color) return;
        const media = heroMedia[color];
        const pre = new Image();
        // sizes before srcset so the browser picks the same candidate the
        // hero <img> will request on swap.
        if (img && img.sizes) pre.sizes = img.sizes;
        if (media.srcset) pre.srcset = media.srcset;
        pre.src = media.src;
      });
    };

    const atIdle = () =>
      window.requestIdleCallback
        ? window.requestIdleCallback(warm, { timeout: 3000 })
        : setTimeout(warm, 1500);

    if (document.readyState === "complete") {
      atIdle();
    } else {
      window.addEventListener("load", atIdle, {
        once: true,
        signal: engine._ac.signal,
      });
    }
  }

  // ── J. Lookbook ───────────────────────────────────────────────────────────
  //
  // The lookbook is a SEPARATE section below the engine (outside this element),
  // so — like the closing CTAs — we find it in the document and guard by-engine
  // (`_fsBoundEngine`) so an editor re-render of EITHER section rebinds cleanly.
  // Every listener/timer/observer rides the engine's AbortController and is torn
  // down on rebind, so init is idempotent.
  //
  // The three server-rendered frames are "slots". A pool is computed from the
  // valid frames (an unresolvable positional colour ref excludes a frame) for
  // the current {color, gender}: gender-mismatched frames are excluded but
  // kept as backfill (never leave a slot empty); order is selected-colour →
  // all-colours → remaining colours (block order preserved). The first N pool
  // frames fill the N slots. The wide banner shows its single best match
  // (colour+gender > colour > all). Colour/gender changes crossfade each
  // changed slot (staggered ~90ms) + the banner; an idle timer advances one
  // slot every ~5s (never in the theme editor).

  function initLookbook(engine) {
    const bind = () => {
      document.querySelectorAll("[data-fs-lookbook]").forEach((section) => {
        if (section._fsBoundEngine === engine) return;
        section._fsBoundEngine = engine;
        bindLookbook(engine, section);
      });
    };
    bind();
    // Editor glue: a re-render of the lookbook (or the engine) yields fresh
    // nodes — rebind them.
    document.addEventListener("shopify:section:load", bind, {
      signal: engine._ac.signal,
    });
  }

  function bindLookbook(engine, section) {
    // The mosaic layout shows its photographs as placed: no slideshow, no
    // re-sorting by colour (a rotation would put a 5:6 frame in the tall slot).
    if (section.hasAttribute("data-fs-lookbook-static")) return;
    const dataEl = section.querySelector("[data-fs-lookbook-data]");
    if (!dataEl) return;
    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }
    const framesAll = Array.isArray(data.frames) ? data.frames : [];
    const banners = Array.isArray(data.banners) ? data.banners : [];
    // Frames whose positional colour ref didn't resolve (e.g. "Colour 3" on a
    // two-colour product) are excluded from the pool entirely.
    const pool = framesAll.filter((f) => !f.invalid && f.src);

    const slots = Array.from(section.querySelectorAll("[data-fs-slot]"));
    const wideEl = section.querySelector("[data-fs-wide]");
    if (!slots.length && !wideEl) return;
    const { signal } = engine._ac;

    // Per-bind state (fresh on every rebind).
    const state = {
      // Which pool index currently occupies each slot. The server rendered
      // the first frame BLOCKS in order, so map them into the (invalid-
      // filtered) pool; an invalid server-rendered frame maps to -1 and is
      // swapped out by the init sync below.
      current: slots.map((_, i) => pool.indexOf(framesAll[i])),
      // Banner index (into `banners`) currently shown — seeded by Liquid via
      // the data-fs-wide attribute (the server-side best match).
      banner: wideEl ? parseInt(wideEl.dataset.fsWide, 10) : -1,
      rr: 0, // round-robin cursor for the idle slideshow
      idleTimer: null,
      visible: true,
      inView: true,
    };

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    // In the theme editor the idle slideshow never runs — cycling would fight
    // the client mid-edit. Selection changes still apply (instantly).
    const designMode = !!(window.Shopify && window.Shopify.designMode);

    // Order the pool for a selection: gender-eligible frames first (selected
    // colour → all-colours → other colours, block order preserved within
    // each), then the excluded (gender-mismatched) frames as backfill so a
    // slot is never left empty.
    function orderedPool(sel) {
      const color = sel.color ? handleize(sel.color) : null;
      const gender = sel.gender ? handleize(sel.gender) : null;
      const genderOk = (f) => !f.gender || !gender || f.gender === gender;
      const rank = (f) => {
        if (color && f.color === color) return 0;
        if (!f.color) return 1;
        return 2;
      };
      const idx = pool.map((f, i) => i);
      const eligible = idx
        .filter((i) => genderOk(pool[i]))
        .sort((a, b) => rank(pool[a]) - rank(pool[b]));
      const backfill = idx.filter((i) => !genderOk(pool[i]));
      return eligible.concat(backfill);
    }

    // Candidate slot→poolIndex assignment (first N of the ordered pool).
    function targetAssignment(sel) {
      if (!pool.length) return state.current.slice();
      const order = orderedPool(sel);
      return slots.map((_, i) => order[i % order.length]);
    }

    // Swap a container (frame slot OR wide banner — same hooks) to `frame`.
    // animate=false → instant (init sync / reduced motion / editor). Only the
    // <img> is replaced; overlays and caption elements persist.
    function setMedia(container, frame, animate) {
      if (!frame) return;
      const timeEl = container.querySelector("[data-fs-slot-time]");
      const capEl = container.querySelector("[data-fs-slot-caption]");
      const chipEl = container.querySelector("[data-fs-chip]");
      const outImg = container.querySelector("[data-fs-slot-img]");

      const swapText = () => {
        if (timeEl) timeEl.textContent = frame.time || "";
        if (capEl) capEl.textContent = frame.caption || "";
        // Editor-only tag chip (rendered under request.design_mode) tracks
        // the block now occupying this container.
        if (chipEl) chipEl.textContent = frame.label || "";
      };

      if (!animate || reduce) {
        if (outImg && frame.src) {
          outImg.srcset = frame.srcset || "";
          if (frame.sizes) outImg.sizes = frame.sizes;
          outImg.src = frame.src;
          outImg.alt = frame.caption || "";
        }
        swapText();
        return;
      }

      // Text: fade out → swap → fade in (~180ms each via CSS).
      [timeEl, capEl].forEach(
        (el) => el && el.classList.add("fs-slot-text-out"),
      );
      setTimeout(() => {
        swapText();
        [timeEl, capEl].forEach(
          (el) => el && el.classList.remove("fs-slot-text-out"),
        );
      }, 180);

      if (!outImg || !frame.src) {
        swapText();
        return;
      }

      // Image: create the incoming img over the outgoing one, decode, then
      // fade incoming 0→1 and outgoing 1→0 on the same frame.
      const inImg = document.createElement("img");
      inImg.className = outImg.className; // same utilities (absolute inset-0 …)
      inImg.classList.add("is-fading-in"); // starts at opacity 0
      inImg.setAttribute("data-fs-slot-img", "");
      inImg.decoding = "async";
      if (frame.sizes) inImg.sizes = frame.sizes;
      if (frame.srcset) inImg.srcset = frame.srcset;
      inImg.src = frame.src;
      inImg.alt = frame.caption || "";
      // Insert as the first child so overlays/captions stay above it.
      container.insertBefore(inImg, container.firstChild);

      const start = () => {
        if (!inImg.isConnected) return;
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          if (outImg.isConnected) outImg.remove();
        };
        outImg.addEventListener("transitionend", finish, { once: true });
        // Safety net: never orphan the outgoing img.
        setTimeout(finish, 900);
        requestAnimationFrame(() => {
          inImg.classList.remove("is-fading-in");
          outImg.classList.add("is-fading-out");
        });
      };

      if (typeof inImg.decode === "function") {
        inImg.decode().then(start, start);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(start));
      }
    }

    // Best banner for a selection: colour+gender (3) > colour (2) >
    // gender-tagged all-colours (1) > all-colours (0). A colour- or gender-
    // tagged banner that doesn't match is not a candidate at all, so with
    // only an "all colours" banner nothing ever swaps. Returns -1 when no
    // candidate exists (→ keep whatever is showing).
    function pickBanner(sel) {
      const color = sel.color ? handleize(sel.color) : null;
      const gender = sel.gender ? handleize(sel.gender) : null;
      let best = -1;
      let bestScore = -1;
      banners.forEach((b, i) => {
        if (b.invalid || !b.src) return;
        if (b.gender && gender && b.gender !== gender) return;
        if (b.color && b.color !== color) return;
        const score = (b.color ? 2 : 0) + (b.gender ? 1 : 0);
        if (score > bestScore) {
          bestScore = score;
          best = i;
        }
      });
      return best;
    }

    function updateBanner(animate) {
      if (!wideEl || !banners.length) return;
      const next = pickBanner(engine.selection);
      if (next < 0 || next === state.banner) return;
      state.banner = next;
      setMedia(wideEl, banners[next], animate);
    }

    // Recompute the pool for the current selection and crossfade any slot
    // whose target frame differs, staggered ~90ms between slots.
    function resort(animate) {
      if (!slots.length || !pool.length) return;
      const target = targetAssignment(engine.selection);
      let delay = 0;
      target.forEach((poolIndex, i) => {
        if (state.current[i] === poolIndex) return;
        const slot = slots[i];
        const idx = poolIndex;
        if (animate && !reduce) {
          setTimeout(() => setMedia(slot, pool[idx], true), delay);
          delay += 90;
        } else {
          setMedia(slot, pool[idx], false);
        }
        state.current[i] = poolIndex;
      });
      state.rr = 0;
    }

    // ── Idle slideshow: advance ONE slot round-robin to the next pool frame
    // not currently visible in any slot. Skips when pool ≤ slot count.
    function idleTick() {
      const order = orderedPool(engine.selection);
      if (order.length <= slots.length) return; // nothing new to rotate in
      const slotI = state.rr % slots.length;
      state.rr = (state.rr + 1) % slots.length;
      const shown = new Set(state.current);
      let next = null;
      for (let k = 0; k < order.length; k += 1) {
        if (!shown.has(order[k])) {
          next = order[k];
          break;
        }
      }
      if (next == null || next === state.current[slotI]) return;
      setMedia(slots[slotI], pool[next], true);
      state.current[slotI] = next;
    }

    function idleRunning() {
      return state.visible && state.inView && !reduce && !designMode;
    }
    function startIdle() {
      if (state.idleTimer || !idleRunning() || !slots.length) return;
      state.idleTimer = setInterval(() => {
        if (!idleRunning()) return;
        idleTick();
      }, 5000);
    }
    function stopIdle() {
      if (state.idleTimer) {
        clearInterval(state.idleTimer);
        state.idleTimer = null;
      }
    }

    // React to colour/gender changes (bubbles up to document from the engine).
    document.addEventListener(
      "flagship:variant:change",
      (evt) => {
        if (!evt.detail || evt.detail.engine !== engine) return;
        const role = evt.detail.changedRole;
        if (role !== "color" && role !== "gender") return;
        resort(true);
        updateBanner(true);
      },
      { signal },
    );

    // Pause when the tab is hidden.
    document.addEventListener(
      "visibilitychange",
      () => {
        state.visible = !document.hidden;
        if (idleRunning()) startIdle();
        else stopIdle();
      },
      { signal },
    );

    // Pause when the section scrolls offscreen.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          state.inView = e.isIntersecting;
        });
        if (idleRunning()) startIdle();
        else stopIdle();
      },
      { threshold: 0.15 },
    );
    io.observe(section);
    signal.addEventListener("abort", () => {
      io.disconnect();
      stopIdle();
    });

    // Init sync: if the server-rendered order/banner differs from the current
    // selection (deep-linked ?variant=, or the engine default differs from
    // selected_or_first_available_variant), fix it instantly, no animation.
    const wanted = targetAssignment(engine.selection);
    if (wanted.some((v, i) => v !== state.current[i])) resort(false);
    updateBanner(false);

    startIdle();
  }

  // ── L. Story header ───────────────────────────────────────────────────────
  //
  // The story layout's own fixed bar. The theme header is hidden on this
  // layout, so this is the page's only navigation: the overlay gets the same
  // treatment as the size drawer (scroll lock, Escape, Tab contained by the
  // theme's trapFocus, focus returned to the burger on close) and reuses the
  // drawer's lock class rather than adding a second mechanism.
  //
  // `lit`, the dark mark on a frosted bar once the light page arrives, reads
  // the hero's end sentinel, not a scroll figure, so it survives a retune of
  // the hero's height. This section also owns the page's other scroll state:
  // the mobile buy bar is out of the way at rest (the hero carries the same
  // CTA at full width) and arrives on the first scroll (`is-story-scrolled`,
  // one class, one rule in flagship-sticky-bar.liquid).

  function initStoryHeader(engine) {
    if (engine.dataset.layout !== "story") return;
    const head = engine.querySelector(".fs-head");
    const menu = engine.querySelector(".fs-menu");
    const toggle = engine.querySelector("[data-fs-menu-toggle]");
    if (!head || !menu || !toggle) return;

    const { signal } = engine._ac;
    const isOpen = () => engine.classList.contains("is-menu-open");

    // The menu's groups (parents with children). One open at a time, and the
    // open one is the button's own aria-expanded; the CSS reads nothing
    // else. Below the seam a group is an accordion the tap toggles. At 760+
    // the children stand in a panel beside the rows, so a group opens on
    // hover or click and never closes on its own, and the first one is open
    // as the menu arrives (a beat after the rows, so the panel follows them
    // in rather than landing first).
    const groups = [...menu.querySelectorAll(".fs-menu-group")];
    const wide = matchMedia("(min-width: 760px)");
    const setGroup = (btn, open) => {
      for (const g of groups)
        g.setAttribute("aria-expanded", String(g === btn && open));
    };
    for (const btn of groups) {
      btn.addEventListener(
        "click",
        () =>
          setGroup(
            btn,
            wide.matches || btn.getAttribute("aria-expanded") !== "true",
          ),
        { signal },
      );
      btn.addEventListener(
        "pointerenter",
        (evt) => {
          if (wide.matches && evt.pointerType === "mouse") setGroup(btn, true);
        },
        { signal },
      );
    }

    function setMenu(open) {
      if (isOpen() === open) return;
      engine.classList.toggle("is-menu-open", open);
      toggle.setAttribute("aria-expanded", String(open));
      document.documentElement.classList.toggle("flagship-drawer-lock", open);
      if (open) {
        window.trapFocus(menu, menu.querySelector("a, button"));
        setGroup(null, false);
        if (wide.matches && groups[0])
          setTimeout(() => isOpen() && setGroup(groups[0], true), 260);
      } else window.removeTrapFocus(toggle);
    }

    toggle.addEventListener("click", () => setMenu(!isOpen()), { signal });
    // Following a link closes the overlay: same-page anchors would otherwise
    // scroll behind a locked, still-open menu.
    menu.addEventListener(
      "click",
      (evt) => {
        if (evt.target.closest("a")) setMenu(false);
      },
      { signal },
    );
    document.addEventListener(
      "keydown",
      (evt) => {
        if (evt.key === "Escape") setMenu(false);
      },
      { signal },
    );
    signal.addEventListener("abort", () => {
      if (!isOpen()) return;
      document.documentElement.classList.remove("flagship-drawer-lock");
      window.removeTrapFocus();
    });

    // The cart count, painted server-side and refreshed on the engine's
    // confirmed on:cart:add only (the same event flagshipAddedSuccess uses;
    // UpCart intercepts theme cart events on this store — docs/gotchas.md —
    // so that event and a pageload are the only promised updates). The
    // change is an in-place settle, quick and smooth.
    const cartCount = engine.querySelector("[data-fs-cart-count]");
    if (cartCount) {
      engine.addEventListener(
        "on:cart:add",
        (evt) => {
          const fromEvent =
            evt.detail && evt.detail.cart && evt.detail.cart.item_count;
          const next = Number.isFinite(fromEvent)
            ? fromEvent
            : (parseInt(cartCount.textContent, 10) || 0) + 1;
          cartCount.textContent = String(next);
          cartCount.hidden = next <= 0;
          cartCount.classList.remove("is-settling");
          void cartCount.offsetWidth; // restart the settle
          cartCount.classList.add("is-settling");
        },
        { signal },
      );
    }

    const sentinel = engine.querySelector("[data-fs-story-end]");
    if (!sentinel) return;

    // At 760 and up the page is light from the first paint (the Frame hero),
    // so the bar earns its frosted panel from the scroll alone; below the
    // seam the hero's end sentinel is the truth, so `lit` survives a retune
    // of the hero's height.
    const seamDesk = matchMedia("(min-width: 760px)");

    let ticking = false;
    function paint() {
      ticking = false;
      engine.classList.toggle(
        "is-story-lit",
        seamDesk.matches
          ? window.scrollY > 16
          : sentinel.getBoundingClientRect().top < head.offsetHeight,
      );
      // The buy bar arrives on the first scroll and steps aside at rest
      // (consumed below the seam only; at 760+ the bar is display: none).
      engine.classList.toggle("is-story-scrolled", window.scrollY > 24);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    }

    addEventListener("scroll", onScroll, { passive: true, signal });
    addEventListener("resize", onScroll, { passive: true, signal });
    seamDesk.addEventListener("change", onScroll, { signal });
    paint();
  }

  // ── The dissolve stack ────────────────────────────────────────────────────
  //
  // One machine, two heroes (the lab's makeStack): the Frame hero's stack at
  // 760 and up (section N) and the simple hero's below the seam (section O)
  // are two instances of this function. Each instance keeps one rule: at
  // every instant of every dissolve there is an opaque frame underneath, and
  // exactly one layer is moving. A frame that is wanted and not lit is
  // raised over the stack and fades IN over the one already covering the
  // plate; a frame that is still lit under the ones that replaced it (the
  // swap-during-a-swap case) is uncovered instead, by fading those OFF it.
  // The alternative, clearing it and fading it back in from the top, empties
  // the plate for the length of the dissolve and the ground shows straight
  // through the picture. Finished frames are dropped to zero underneath the
  // live one, unanimated, once it has settled.
  //
  // `wanted()` answers with the layer the engine's current selection asks
  // for (each caller owns its own key scheme and fallback); `after(el)` runs
  // as a swap starts, for state that must ride in with its own picture (the
  // red grade pools).

  function makeFlagshipStack(shots, { signal, wanted, after }) {
    let live = shots[0] || null;
    const stack = shots.slice(1).concat(live ? [live] : []);
    let settle = 0;
    let settleFrame = 0;

    function raise(el) {
      stack.splice(stack.indexOf(el), 1);
      stack.push(el);
      stack.forEach((layer, n) => {
        layer.style.zIndex = n;
      });
    }
    if (live) raise(live);

    // To zero with no transition of its own: the frame is invisible at that
    // moment, under an opaque one, and a second animation there is a second
    // thing that can be caught half way.
    function douse(el) {
      if (!el.classList.contains("live")) return;
      el.classList.add("cut");
      el.classList.remove("live");
      void el.offsetWidth;
      el.classList.remove("cut");
    }
    function show(el) {
      if (!el || el === live) return;
      live = el;
      if (el.classList.contains("live")) {
        shots.forEach((layer) => {
          if (layer !== el) layer.classList.remove("live");
        });
      } else {
        raise(el);
        el.classList.add("live");
      }
      if (after) after(el);
      // The length is read off the element, so the number lives in the
      // stylesheet once and reduced motion shortens the sweep with it. It is
      // read on the next frame: inside a colour pick the computed style
      // would force the page's colour restyle into the tap.
      clearTimeout(settle);
      cancelAnimationFrame(settleFrame);
      settleFrame = requestAnimationFrame(() => {
        const ms =
          parseFloat(getComputedStyle(el).transitionDuration) * 1000 + 80;
        settle = setTimeout(() => {
          shots.forEach((layer) => {
            if (layer !== live) douse(layer);
          });
        }, ms);
      });
    }
    // A half-decoded picture crossfading is worse than a late one, so a
    // layer with no src yet arms its own dissolve on load, and only if it
    // is still the one wanted.
    function prime(el) {
      if (!el || !el.dataset.src || el.src) return;
      el.addEventListener(
        "load",
        () => {
          if (el === wanted()) show(el);
        },
        { once: true, signal },
      );
      el.src = el.dataset.src;
    }
    return {
      prime,
      want() {
        const el = wanted();
        prime(el);
        if (el && el.src && el.complete && el.naturalWidth) show(el);
      },
    };
  }

  // ── N. Frame hero ─────────────────────────────────────────────────────────
  //
  // The 760+ half of the story layout: the framed print with the buying set
  // on the glass (flagship-hero-frame.liquid), ported verbatim from the Frame
  // half of lab/lumis/full.html. No-op unless the layout is story; the
  // dissolve additionally no-ops below the 760 seam, where the simple hero
  // (section O) owns the page.
  //
  // The engine stays the single owner of {color, gender, size}: every control
  // here is a button that WRITES to the engine and one sync() reads it all
  // back on flagship:variant:change — never a second radio set, so the
  // drawer, the sticky bar and the post-hero radios can never disagree.
  //
  // The hero is keyed by colour AND fit. All four colours have wide
  // photography for the women's fit; a men's key falls back to its
  // colourway's women's frame (the lab's documented fallback). Only the
  // first frame is in the
  // first paint's budget; the rest are fetched on intent and their dissolve
  // is armed on load. The stack discipline lives in makeFlagshipStack above.

  function initFrameHero(engine) {
    if (engine.dataset.layout !== "story") return;
    const frame = engine.querySelector("[data-fs-frame]");
    if (!frame) return;
    const { signal } = engine._ac;
    const seamDesk = matchMedia("(min-width: 760px)");

    // Strip the intro animations once they are done: blur(0px) is not
    // `none`, the fill mode is `both`, and a surviving filter would cost the
    // size squares and the sale badge their backdrop for the rest of the
    // page (they would sample an empty layer instead of the photograph).
    const shots = Array.from(frame.querySelectorAll(".fsf-shot"));
    const firstShot = shots[0] || null;
    if (firstShot) {
      firstShot.addEventListener(
        "animationend",
        () => {
          firstShot.style.animation = "none";
        },
        { once: true, signal },
      );
    }
    frame.querySelectorAll(".fsf-rise").forEach((el) => {
      el.addEventListener(
        "animationend",
        () => el.classList.remove("fsf-rise"),
        { once: true, signal },
      );
    });

    // ── The dissolve (the shared stack; the keys read the engine) ──
    // A layer's key is "<colour handle>-<fit>", the engine's own data-color
    // and data-gender joined, so every story template (Lumis, the nurse
    // Novera) declares its photographs in the snippet and nothing here
    // names a colour.
    function keyFit() {
      return engine.dataset.gender === "men" ? "men" : "women";
    }
    function key() {
      return (engine.dataset.color || "") + "-" + keyFit();
    }
    function frameFor(k) {
      // A key with no layer falls back to the women's frame of its
      // colourway, so the colour dissolve keeps working while the men's
      // photography is pending, and then to the first layer, so a colour
      // with no photograph yet keeps the last picture instead of a blank.
      return (
        shots.find((layer) => layer.dataset.key === k) ||
        shots.find(
          (layer) => layer.dataset.key === k.replace("-men", "-women"),
        ) ||
        firstShot
      );
    }
    const stack = makeFlagshipStack(shots, {
      signal,
      wanted: () => frameFor(key()),
      // A layer that declares data-pool="red" is daylight photography (the
      // Lumis Scarlet frames); its pool rides in on the same curve, and
      // only now, with the picture it belongs to. The rest ride the night
      // grade.
      after: (el) =>
        engine.classList.toggle("is-hero-red", el.dataset.pool === "red"),
    });
    function want() {
      if (!seamDesk.matches) return;
      stack.want();
    }

    // ── The controls: buttons into the engine ──
    const xrow = frame.querySelector('[data-fsf-option="color"]');
    const seg = frame.querySelector('[data-fsf-option="gender"]');
    const ind = seg ? seg.querySelector(".fsf-seg-ind") : null;
    const pills = Array.from(frame.querySelectorAll(".fsf-xpill"));
    const segBtns = seg
      ? Array.from(seg.querySelectorAll("button[data-fsf-value]"))
      : [];
    const sizes = Array.from(frame.querySelectorAll(".fsf-sq"));
    const sizeGrid = frame.querySelector(".fsf-grid");

    pills.forEach((btn) => {
      btn.addEventListener(
        "click",
        () => engine.setOption("color", btn.dataset.fsfValue),
        { signal },
      );
    });
    segBtns.forEach((btn) => {
      btn.addEventListener(
        "click",
        () => engine.setOption("gender", btn.dataset.fsfValue),
        { signal },
      );
    });
    sizes.forEach((btn) => {
      btn.addEventListener(
        "click",
        () => engine.setOption("size", btn.dataset.fsfValue),
        { signal },
      );
    });

    // The frames each control can reach in a single click, fetched when
    // the pointer arrives on that control (the colour row can reach every
    // other colour's frame). Without a pointer the click's want() primes
    // the one chosen instead and the dissolve waits for the load.
    if (xrow) {
      xrow.addEventListener(
        "pointerenter",
        () => {
          if (!seamDesk.matches) return;
          ["black", "red", "gray", "sage"].forEach((color) => {
            if (color !== keyColor())
              stack.prime(frameFor(color + "-" + keyFit()));
          });
        },
        { signal },
      );
    }
    if (seg) {
      seg.addEventListener(
        "pointerenter",
        () => {
          if (!seamDesk.matches) return;
          stack.prime(
            frameFor(
              keyColor() +
                "-" +
                (engine.dataset.gender === "men" ? "women" : "men"),
            ),
          );
        },
        { signal },
      );
    }

    // ── The ATC and the money ──
    const atc = frame.querySelector("[data-fsf-atc]");
    const atcNow = atc && atc.querySelector(".now");
    const mainAtc = engine.querySelector('[name="add"]');
    const priceEl = frame.querySelector("[data-fsf-price]");
    const wasEl = frame.querySelector("[data-fsf-was]");
    const dealEl = frame.querySelector("[data-fsf-deal]");

    if (atc) {
      atc.addEventListener(
        "click",
        () => {
          if (!engine.selection.size) {
            if (sizeGrid) {
              sizeGrid.classList.remove("shake");
              void sizeGrid.offsetWidth; // restart the animation
              sizeGrid.classList.add("shake");
            }
            return;
          }
          if (atc.getAttribute("aria-disabled") === "true") return;
          if (atc.classList.contains("added")) return;
          submitMainForm(engine);
        },
        { signal },
      );
      // The Added morph rides the CONFIRMED add only, like the sticky bar
      // and the drawer (flagshipAddedSuccess) — never the click.
      engine.addEventListener(
        "on:cart:add",
        () => {
          atc.classList.add("added");
          setTimeout(() => atc.classList.remove("added"), 1900);
        },
        { signal },
      );
    }

    function sync() {
      const sel = engine.selection;

      const availColor = engine.availableValues("color");
      pills.forEach((btn) => {
        const value = btn.dataset.fsfValue;
        const on = value === sel.color;
        btn.classList.toggle("on", on);
        btn.setAttribute("aria-checked", String(on));
        btn.classList.toggle("is-unavailable", !availColor.has(value));
      });

      if (seg) {
        const availGender = engine.availableValues("gender");
        segBtns.forEach((btn, n) => {
          const value = btn.dataset.fsfValue;
          const on = value === sel.gender;
          btn.classList.toggle("on", on);
          btn.setAttribute("aria-checked", String(on));
          btn.disabled = !availGender.has(value);
          if (on && ind) ind.style.transform = `translateX(${n * 100}%)`;
        });
      }

      // [disabled] hides the square (CSS in the snippet): an unavailable
      // size here is the other fit's, not a sell-out, so the grid shows the
      // current fit's run — same convention as the panel and the drawer.
      const availSize = engine.availableValues("size");
      sizes.forEach((btn) => {
        const value = btn.dataset.fsfValue;
        const off = !availSize.has(value);
        const on = !off && value === sel.size;
        btn.disabled = off;
        btn.classList.toggle("on", on);
        btn.setAttribute("aria-checked", String(on));
      });

      const rep = repVariant(engine);
      if (rep && rep.price && priceEl) {
        priceEl.textContent = rep.price;
        if (wasEl) {
          wasEl.textContent = rep.compareAt || "";
          wasEl.hidden = !rep.compareAt;
        }
        if (dealEl) dealEl.hidden = !rep.compareAt;
      }

      // ATC state from the main button's server-rendered data-* labels, so
      // the strings stay localised. Only a resolved sold-out variant
      // disables: with no size chosen the button answers with the shake.
      if (atc && atcNow) {
        const variant = engine.variant;
        let text;
        if (!variant) {
          text = (mainAtc && mainAtc.dataset.preselectionText) || "Select Size";
        } else if (variant.available) {
          text = (mainAtc && mainAtc.dataset.addToCartText) || "Add to Cart";
        } else {
          text = (mainAtc && mainAtc.dataset.soldoutText) || "Sold out";
        }
        if (atcNow.textContent.trim() !== text) atcNow.textContent = text;
        if (variant && !variant.available) {
          atc.setAttribute("aria-disabled", "true");
        } else {
          atc.removeAttribute("aria-disabled");
        }
      }
    }

    engine.addEventListener(
      "flagship:variant:change",
      (evt) => {
        sync();
        const role = evt.detail && evt.detail.changedRole;
        if (role === "color" || role === "gender") want();
      },
      { signal },
    );
    sync();

    // This half's own first paint, and its own waking: the src is written
    // when the half is live, onto a fetch the media-gated preload already
    // started. --fsf-band assumes the print starts at document offset 0
    // (the story layout hides the theme header group — the written
    // guarantee); log when that is ever untrue.
    function wake() {
      if (!seamDesk.matches) return;
      if (firstShot && !firstShot.getAttribute("src")) {
        firstShot.src = firstShot.dataset.src;
      }
      if (Math.abs(frame.getBoundingClientRect().top + window.scrollY) >= 4) {
        engine._log("frame hero not at document offset 0 — band geometry off");
      }
      want();
    }
    wake();
    seamDesk.addEventListener("change", wake, { signal });
    requestAnimationFrame(() => engine.classList.add("is-frame-go"));
  }

  // ── O. Simple hero ────────────────────────────────────────────────────────
  //
  // The below-760 half of the story layout: one portrait photograph filling
  // the first screen with the colour picker and the ghost size CTA standing
  // in it (flagship-hero-simple.liquid), ported verbatim from
  // lab/lumis/simple.html. No-op unless the layout is story; the dissolve
  // additionally no-ops at 760 and up, where the Frame hero (section N) owns
  // the page.
  //
  // The dissolve is the shared stack machine, keyed by the engine's colour
  // handle; a colour with no portrait keeps the black frame silently (the
  // lab's documented fallback) until its photography arrives.
  //
  // This section also owns the OTHER picker below the seam, the chips under
  // the gallery: both are [data-fss-value] buttons that write to the engine,
  // and one sync() reads it back on flagship:variant:change and names the
  // live colour in every [data-fss-color-name] line, so the two rows and the
  // drawer can never disagree. One picker machine, two rows, no second
  // mechanism. The ghost CTA is section B's [data-flagship-select-size] hook:
  // it opens the real size drawer while browsing and adds to cart once a size
  // is chosen.

  function initSimpleHero(engine) {
    if (engine.dataset.layout !== "story") return;
    const hero = engine.querySelector("[data-fs-simple]");
    if (!hero) return;
    const { signal } = engine._ac;
    const seamMob = matchMedia("(max-width: 759.98px)");

    // Strip the intro animations once they are done: blur(0px) is not
    // `none`, the fill mode is `both`, and a surviving filter on the rise
    // elements would cost the ghost CTA its glass; one on the shot would be
    // re-evaluated for the whole scroll.
    const shots = Array.from(hero.querySelectorAll(".fss-shot"));
    const firstShot = shots[0] || null;
    if (firstShot) {
      firstShot.addEventListener(
        "animationend",
        () => {
          firstShot.style.animation = "none";
        },
        { once: true, signal },
      );
    }
    hero.querySelectorAll(".fss-rise").forEach((el) => {
      el.addEventListener(
        "animationend",
        () => el.classList.remove("fss-rise"),
        { once: true, signal },
      );
    });

    // ── The dissolve (the shared stack; the key is the engine's colour) ──
    // A layer's key is the colour handle, the engine's own data-color; a
    // colour with no layer keeps the first photograph. The snippet declares
    // the layers per template, so nothing here names a colour.
    const layerFor = (k) =>
      shots.find((layer) => layer.dataset.key === k) || firstShot;
    const stack = makeFlagshipStack(shots, {
      signal,
      wanted: () => layerFor(engine.dataset.color),
      // A layer with data-pool="red" brings its daylight pool with it,
      // never ahead of it.
      after: (el) =>
        engine.classList.toggle("is-hero-red", el.dataset.pool === "red"),
    });
    function want() {
      if (!seamMob.matches) return;
      stack.want();
    }

    // ── The pickers: buttons into the engine (the hero's row and the chips
    //    under the gallery, bound as one) ──
    const pills = Array.from(engine.querySelectorAll("[data-fss-value]"));
    const nameEls = Array.from(
      engine.querySelectorAll("[data-fss-color-name]"),
    );
    pills.forEach((btn) => {
      btn.addEventListener(
        "click",
        () => engine.setOption("color", btn.dataset.fssValue),
        { signal },
      );
    });
    function sync() {
      const sel = engine.selection;
      const avail = engine.availableValues("color");
      pills.forEach((btn) => {
        const value = btn.dataset.fssValue;
        const on = value === sel.color;
        btn.classList.toggle("on", on);
        btn.setAttribute("aria-checked", String(on));
        btn.classList.toggle("is-unavailable", !avail.has(value));
      });
      if (sel.color) nameEls.forEach((el) => (el.textContent = sel.color));
    }
    engine.addEventListener(
      "flagship:variant:change",
      (evt) => {
        sync();
        if (evt.detail && evt.detail.changedRole === "color") want();
      },
      { signal },
    );
    sync();

    // The frames that are not in the first paint's budget. On a phone there
    // is no hover worth the name, so the Scarlet, Pearl and Sage layers are
    // fetched once the page is quiet; a pointer that does reach the row
    // primes them too.
    function primeAlts() {
      if (!seamMob.matches) return;
      stack.prime(layerFor("scarlet-red"));
      stack.prime(layerFor("pearl-gray"));
      stack.prime(layerFor("sage-beige"));
    }
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 600));
    if (document.readyState === "complete") {
      idle(primeAlts);
    } else {
      addEventListener("load", () => idle(primeAlts), {
        once: true,
        signal,
      });
    }
    const xrow = hero.querySelector('[data-fss-option="color"]');
    if (xrow) xrow.addEventListener("pointerenter", primeAlts, { signal });

    // This half's own first paint, and its own waking: the src is written
    // when the half is live, onto the fetch the media-gated preload already
    // started. A crossing back re-derives the wanted frame from the engine.
    function wake() {
      if (!seamMob.matches) return;
      if (firstShot && !firstShot.getAttribute("src")) {
        firstShot.src = firstShot.dataset.src;
      }
      want();
    }
    wake();
    seamMob.addEventListener("change", wake, { signal });
    requestAnimationFrame(() => engine.classList.add("is-simple-go"));
  }

  // ── P. Size guide ─────────────────────────────────────────────────────────
  // Every size surface (details panel, Frame hero, drawer, the standard page's
  // size row) carries the template's own size guide link,
  // [data-fs-size-guide], and a tap on it is always a pop-up, never a page:
  // leaving the PDP for a size table is a lost sale. On a store with Kiwi
  // Sizing (US), Kiwi injects its own "Size Chart" link into the first
  // fieldset[data-option="Size"]; that link is hidden by the page chrome CSS
  // and, on the flagship templates, ours opens Kiwi's modal by clicking it
  // when it is on the page by the time of the tap. Otherwise (no Kiwi, Kiwi
  // not there yet, or the standard page, which always takes the native guide)
  // the theme's own modal-dialog with the size guide page's content opens,
  // the same pop-up the classic templates use; the section renders it
  // whenever the page is set. The native guide's Women/Men tabs are radios
  // whose ids end in the fit (#ow-sg-women, #ow-sg-men), so the guide opens
  // on the fit the engine holds where the product has one. The href only
  // ever serves a browser without JS.
  function initSizeGuide(engine) {
    const nativeOnly =
      engine.dataset.layout === "standard" ||
      engine.dataset.layout === "showroom";
    engine.addEventListener(
      "click",
      (evt) => {
        const link = evt.target.closest("[data-fs-size-guide]");
        if (!link) return;
        const kiwi = nativeOnly
          ? null
          : document.querySelector(".ks-chart-modal-link");
        const modal = document.getElementById(link.dataset.fsSizeGuide);
        if (!kiwi && !(modal && modal.open)) return;
        evt.preventDefault();
        if (kiwi) {
          kiwi.click();
          return;
        }
        const gender = engine.selection.gender;
        if (gender) {
          const tab = modal.querySelector(
            `input[type="radio"][id$="-${CSS.escape(handleize(gender))}"]`,
          );
          if (tab) tab.checked = true;
        }
        modal.open(link);
      },
      { signal: engine._ac.signal },
    );
  }

  // ── T. Pill fit ───────────────────────────────────────────────────────────
  // Every K2 pill group ([data-xpill-fit]: the hero, the drawer, the story
  // gallery, a showroom chapter) shows as much of each colour name as its
  // width allows, on one line: "full" (every pill named), "short" (the chosen
  // pill named, the others by their last word) or "dot" (the others as
  // circles). Each pill's width in each fit is read once with transitions
  // off (data-xpill-measure); a fit is taken when the pills, with the widest
  // of them shown whole, fit the group. So the fit holds whichever colour is
  // chosen, and only the group's own width changes it (ResizeObserver).
  // The states are CSS (snippets/flagship-xpill.liquid).
  const PILL_FITS = ["full", "short", "dot"];
  function fitPills(group) {
    const width = group.clientWidth;
    if (!width) return;
    const pills = Array.from(group.querySelectorAll(".fs-xpill")).filter(
      (p) => getComputedStyle(p).display !== "none",
    );
    if (!pills.length) return;
    const style = getComputedStyle(group);
    const gap = parseFloat(style.columnGap) || 0;
    const before = group.dataset.xpillFit;
    group.setAttribute("data-xpill-measure", "");
    const widths = {};
    PILL_FITS.forEach((fit) => {
      group.dataset.xpillFit = fit;
      widths[fit] = pills.map((p) => p.getBoundingClientRect().width);
    });
    // Back to the fit it had, still unanimated, then let transitions run, so
    // only a real change of fit animates.
    group.dataset.xpillFit = before;
    void group.offsetWidth;
    group.removeAttribute("data-xpill-measure");
    void group.offsetWidth;
    const gaps = gap * (pills.length - 1);
    const need = (fit) =>
      widths[fit].reduce((sum, w) => sum + w, 0) +
      Math.max(0, ...widths.full.map((w, i) => w - widths[fit][i])) +
      gaps;
    const fit = PILL_FITS.find((f) => need(f) <= width + 0.5) || "dot";
    if (fit !== before) group.dataset.xpillFit = fit;
  }
  function initPillFit(engine) {
    const groups = engine.querySelectorAll("[data-xpill-fit]");
    if (!groups.length || typeof ResizeObserver !== "function") return;
    const seen = new WeakMap();
    const ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const w = Math.round(entry.contentRect.width);
        if (seen.get(entry.target) === w) return;
        seen.set(entry.target, w);
        fitPills(entry.target);
      });
    });
    groups.forEach((g) => ro.observe(g));
    engine._ac.signal.addEventListener("abort", () => ro.disconnect());
    // Web fonts change every name's width once they arrive.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!engine._ac.signal.aborted) groups.forEach(fitPills);
      });
    }
  }

  // ── S. Colour name ────────────────────────────────────────────────────────
  // Any [data-fs-color-name] inside the engine (the standard page's colour
  // legend and its drawer label, where colour is a row of photographs with
  // no words on them) reads the live colour.
  function initColorName(engine) {
    const els = engine.querySelectorAll("[data-fs-color-name]");
    if (!els.length) return;
    engine.addEventListener(
      "flagship:variant:change",
      () => {
        const { color } = engine.selection;
        if (color) els.forEach((el) => (el.textContent = color));
      },
      { signal: engine._ac.signal },
    );
  }

  // ── Q. Reviews link ───────────────────────────────────────────────────────
  // The rating row's score and count ([data-fs-reviews]) scroll smoothly to
  // the section holding the Loox widget. The widget's id differs by install
  // (#looxReviews, #loox-reviews-feed), so both are looked up and the scroll
  // lands on the section, clear of the story layout's fixed bar or the
  // theme's sticky header. No widget on
  // the page (reviews app off, section removed): the tap does nothing. The
  // theme's own smooth scroll for "#" links (main.js) is stopped: it aims
  // 100px above the widget and knows nothing of the story bar.
  function initReviewsLink(engine) {
    engine.addEventListener(
      "click",
      (evt) => {
        if (!evt.target.closest("[data-fs-reviews]")) return;
        evt.preventDefault();
        evt.stopPropagation();
        const widget = document.querySelector(
          "#looxReviews, #loox-reviews-feed",
        );
        if (!widget) return;
        const target = widget.closest(".shopify-section") || widget;
        // Clear whichever bar is fixed over the page: the story layout's own,
        // or the theme's sticky header on the other layouts.
        const bar = engine.querySelector(".fs-head");
        const themeBar =
          !bar && window.theme && typeof theme.stickyHeaderHeight === "function"
            ? theme.stickyHeaderHeight()
            : 0;
        const top =
          target.getBoundingClientRect().top +
          window.scrollY -
          (bar ? bar.offsetHeight : themeBar);
        const still = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;
        window.scrollTo({ top, behavior: still ? "instant" : "smooth" });
      },
      { signal: engine._ac.signal },
    );
  }

  // ── R. Price row ──────────────────────────────────────────────────────────
  // The details panel's price row (product-price-custom.liquid) shows the
  // same variant as the Frame's money (repVariant). The struck price and the
  // sale badge show only while that variant's compareAt is set, which the
  // engine data leaves null unless it beats the price.
  function initPriceRow(engine) {
    const rows = engine.querySelectorAll("[data-price-row]");
    if (!rows.length) return;
    const update = () => {
      const rep = repVariant(engine);
      if (!rep || !rep.price) return;
      rows.forEach((row) => {
        const now = row.querySelector("[data-price-now]");
        const was = row.querySelector("[data-price-was]");
        const deal = row.querySelector("[data-price-deal]");
        if (now) now.textContent = rep.price;
        if (was) {
          was.textContent = rep.compareAt || "";
          was.hidden = !rep.compareAt;
        }
        if (deal) deal.hidden = !rep.compareAt;
      });
    };
    engine.addEventListener("flagship:variant:change", update, {
      signal: engine._ac.signal,
    });
    update();
  }

  // ── M. Carousel lightbox ──────────────────────────────────────────────────
  //
  // Tapping a carousel slide opens it full screen: a scroll-snap track of the
  // media the current colour shows, pinch or double tap to look closer, swipe
  // left and right for the rest of the set, and the X, Escape, a swipe down or
  // the back button to leave. Videos are in the set in carousel order, as the
  // viewer's own <video> built from the carousel one, playing while it is the
  // slide being looked at and paused the moment it is not. Nothing zooms on a
  // video: it is footage, not a picture to inspect.
  //
  // <flagship-lightbox> owns everything about being open the way
  // <flagship-drawer> does (focus, the shared `flagship-drawer-lock` scroll
  // lock, one pushed history entry, the gestures); initCarouselLightbox only
  // wires the carousel's taps to it on the engine's AbortController. The track
  // is filled at open time, so nothing is downloaded until someone asks and
  // the colour filter needs no second implementation. The engine also carries
  // `is-lightbox-open` for as long as the viewer is up, which is what moves
  // the page's own fixed chrome out of its way (flagship-carousel.liquid says
  // why a z-index of 10000 does not do that on its own).
  //
  // The opening image grows from its carousel rectangle: a FLIP transform on
  // the slide's zoom wrapper, 320ms, reversed on close. Reduced motion gets a
  // plain fade instead (the CSS drops the FLIP too).

  const prefersReduce = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Largest candidate of the carousel image's own srcset ("… 1400w"), which is
  // the full-size source the CDN already has warm from the thumbnail request.
  function largestSrcsetUrl(img) {
    const best = (img.getAttribute("srcset") || "")
      .split(",")
      .map((candidate) => candidate.trim().split(/\s+/))
      .filter((parts) => parts[0])
      .reduce(
        (winner, parts) =>
          parseInt(parts[1], 10) > parseInt(winner[1], 10) ? parts : winner,
        ["", "0"],
      );
    return best[0] || img.currentSrc || img.src;
  }

  // The viewer's copy of a carousel image: the largest candidate, and no drag
  // behaviour of its own.
  function lightboxImage(source, eager) {
    const img = document.createElement("img");
    img.className = "fs-lb-img";
    img.src = largestSrcsetUrl(source);
    img.alt = source.alt || "";
    img.decoding = "async";
    img.loading = eager ? "eager" : "lazy";
    // A mouse drag on an <img> starts an HTML5 image drag, which cancels the
    // pointer sequence mid-gesture: no pan, no swipe-down on desktop.
    img.draggable = false;
    return img;
  }

  // The viewer's copy of a carousel video, built element by element rather
  // than cloned. A cloned <video> carries its <source> children as markup but
  // no selected resource (networkState NETWORK_NO_SOURCE, currentSrc empty),
  // and Safari does not run the resource selection algorithm again for it: the
  // element stays without a resource, which also keeps it out of the poster
  // frame state, so the slide painted nothing at all until a later play() call
  // forced the selection. Built and load()ed here, the state is the same on
  // every engine and on the first frame.
  //
  // Section H's contract is restated on purpose (muted, playsinline, loop,
  // preload="none", never native controls); flagship-carousel.liquid holds the
  // same list for the carousel copy.
  //
  // Two more things the box needs before anything is fetched: the ratio the
  // slide carries (preload="none" leaves the element with no intrinsic size
  // when the FLIP measures it a moment later) and the poster as a background,
  // which paints from the first frame whether or not the media element has
  // reached a state where it would draw the poster itself.
  function lightboxVideo(source) {
    const video = document.createElement("video");
    video.className = "fs-lb-video";
    video.muted = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.loop = true;
    video.controls = false;
    video.preload = "none";

    const poster = source.poster;
    if (poster) video.poster = poster;
    const label = source.getAttribute("aria-label");
    if (label) video.setAttribute("aria-label", label);

    source.querySelectorAll("source").forEach((child) => {
      const copy = document.createElement("source");
      copy.src = child.src;
      if (child.type) copy.type = child.type;
      video.append(copy);
    });
    if (!video.firstChild && source.currentSrc) video.src = source.currentSrc;
    // The explicit invocation of the media load algorithm. This is the line
    // the clone was missing: it selects the source and puts the element in
    // its poster frame state, before it is even in the document.
    video.load();

    const slide = source.closest(".carousel-slide");
    const ratio = slide && parseFloat(slide.dataset.fsRatio);
    if (ratio) video.style.setProperty("--fs-lb-ratio", ratio);
    // A CDN URL never carries either character; anything else is not a URL we
    // put in a stylesheet.
    if (poster && !/["\\]/.test(poster)) {
      video.style.backgroundImage = `url("${poster}")`;
    }
    return video;
  }

  // The transform that puts `el` (currently occupying `to`) over `from`.
  function flipTransform(from, to) {
    const dx = from.left + from.width / 2 - (to.left + to.width / 2);
    const dy = from.top + from.height / 2 - (to.top + to.height / 2);
    const scale = to.width ? from.width / to.width : 1;
    return `translate(${dx}px, ${dy}px) scale(${scale})`;
  }

  class FlagshipLightbox extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      const { signal } = this._ac;
      this._track = this.querySelector("[data-fs-lb-track]");
      this._sources = [];
      this._index = 0;
      this._blocked = new Set();
      this._resetZoom();

      this.addEventListener(
        "click",
        (evt) => {
          if (evt.target.closest("[data-fs-lb-close]")) this.close();
          else if (evt.target.closest("[data-fs-lb-prev]")) this.go(-1);
          else if (evt.target.closest("[data-fs-lb-next]")) this.go(1);
        },
        { signal },
      );

      // Any real user gesture lifts an OS autoplay refusal, so every one of
      // them is also a retry for the video on screen (section H does the same).
      const retry = () => this._retryBlocked();
      this.addEventListener("click", retry, { signal });
      this.addEventListener("touchend", retry, { passive: true, signal });

      document.addEventListener(
        "keydown",
        (evt) => {
          if (!this._isOpen) return;
          if (evt.key === "Escape") this.close();
          else if (evt.key === "ArrowLeft") this.go(-1);
          else if (evt.key === "ArrowRight") this.go(1);
        },
        { signal },
      );

      // The back button is the fourth way out: open pushes an entry, close
      // pops it, and a real back press lands here with the entry already gone.
      window.addEventListener(
        "popstate",
        () => {
          if (!this._isOpen) return;
          this._pushed = false;
          this.close();
        },
        { signal },
      );

      if (!this._track) return;
      this._track.addEventListener("scrollend", () => this._readIndex(), {
        passive: true,
        signal,
      });
      // Fallback for browsers without scrollend, same shape as the carousel's.
      this._track.addEventListener(
        "scroll",
        () => {
          clearTimeout(this._scrollTimer);
          this._scrollTimer = setTimeout(() => this._readIndex(), 120);
        },
        { passive: true, signal },
      );
      this._bindGestures(signal);
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._isOpen) {
        document.documentElement.classList.remove("flagship-drawer-lock");
        window.removeTrapFocus();
      }
    }

    /** `media` are the carousel <img>/<video>s to show; `opener` gets focus back. */
    open(media, index, opener) {
      if (this._isOpen || !this._track || !media.length) return;
      this._isOpen = true;
      // A reopen inside the closing animation inherits its pending teardown.
      clearTimeout(this._closeTimer);
      this.classList.remove("is-closing");
      this._opener = opener && opener.focus ? opener : null;
      this._sources = media;
      this._index = Math.max(0, Math.min(media.length - 1, index));
      this._resetZoom();
      this._blocked = new Set();

      this._track.replaceChildren(
        ...media.map((source, i) => {
          const slide = document.createElement("div");
          slide.className = "fs-lb-slide";
          const zoom = document.createElement("div");
          zoom.className = "fs-lb-zoom";
          zoom.append(
            source.tagName === "VIDEO"
              ? lightboxVideo(source)
              : lightboxImage(source, i === this._index),
          );
          slide.append(zoom);
          return slide;
        }),
      );

      this.classList.add("is-tracking", "is-open");
      this.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("flagship-drawer-lock");
      // The page's fixed chrome outranks the viewer wherever an ancestor of
      // the carousel is a stacking context (the story column is), so it steps
      // aside on this class instead. The rules are with the lightbox CSS.
      const engine = this.closest("flagship-product");
      if (engine) {
        engine.classList.add("is-lightbox-open");
        // Section H reads the class and parks the carousel's own videos: the
        // viewer plays its copy, and two of the same clip must not run at once.
        engine.dispatchEvent(new CustomEvent("fs-lightbox"));
      }
      this._track.scrollTo({
        left: this._track.clientWidth * this._index,
        behavior: "instant",
      });
      this._paint();

      const closeBtn = this.querySelector("[data-fs-lb-close]");
      window.trapFocus(this, closeBtn || this);

      try {
        history.pushState({ fsLb: this.id }, "", location.href);
        this._pushed = true;
      } catch {
        /* History unavailable — the other three exits still close it */
      }

      const zoom = this._zoomAt(this._index);
      const from = media[this._index].getBoundingClientRect();
      const flip =
        zoom && from.width && !prefersReduce()
          ? flipTransform(from, zoom.getBoundingClientRect())
          : null;
      if (flip) {
        zoom.style.transform = flip;
        // Synchronous reflow, not rAF: the start frame has to be committed
        // before the class that suppresses the transition comes off.
        void this.offsetHeight;
      }
      this.classList.remove("is-tracking");
      if (flip) zoom.style.transform = "";

      // The first attempt runs while the track is still being seated. Once the
      // browser has settled the snap position, ask again: a video that lost
      // that race is not left sitting on its poster.
      requestAnimationFrame(() => {
        if (this._isOpen) this._syncVideos();
      });
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;
      if (this._pushed) {
        this._pushed = false;
        history.back();
      }
      window.removeTrapFocus(this._opener);
      this._opener = null;
      this._resetZoom();
      // Silent for the whole flight back, not just once the track is emptied.
      this._track.querySelectorAll("video").forEach((video) => video.pause());

      // Land the carousel on the media being looked at: the FLIP needs a real
      // rectangle to fly back to, and the carousel should agree with what the
      // shopper just browsed.
      const source = this._sources[this._index];
      const engine = this.closest("flagship-product");
      const slide = source && source.closest(".carousel-slide");
      if (engine && slide) {
        const at = getVisibleSlides(engine).indexOf(slide);
        if (at >= 0) scrollToSlideIndex(engine, at, "instant");
      }

      const zoom = this._zoomAt(this._index);
      const to = source ? source.getBoundingClientRect() : null;
      this.classList.add("is-closing");
      if (zoom && to && to.width && !prefersReduce()) {
        zoom.style.transform = flipTransform(to, zoom.getBoundingClientRect());
      }

      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(
        () => {
          this.classList.remove("is-open", "is-closing");
          this.setAttribute("aria-hidden", "true");
          this._track.replaceChildren();
          this._sources = [];
          document.documentElement.classList.remove("flagship-drawer-lock");
          if (engine) {
            engine.classList.remove("is-lightbox-open");
            // Section H resumes whatever the carousel has on screen.
            engine.dispatchEvent(new CustomEvent("fs-lightbox"));
          }
        },
        prefersReduce() ? 160 : 320,
      );
    }

    /** Step by `delta` slides; the ends do not wrap. */
    go(delta) {
      const next = this._index + delta;
      if (!this._track || next < 0 || next >= this._sources.length) return;
      this._resetZoom();
      this._index = next;
      this._track.scrollTo({
        left: this._track.clientWidth * next,
        behavior: prefersReduce() ? "instant" : "smooth",
      });
      this._paint();
    }

    _zoomAt(index) {
      const slide = this._track && this._track.children[index];
      return slide ? slide.querySelector(".fs-lb-zoom") : null;
    }

    _readIndex() {
      if (!this._isOpen || !this._track.clientWidth) return;
      const at = Math.round(this._track.scrollLeft / this._track.clientWidth);
      if (at === this._index || at < 0 || at >= this._sources.length) return;
      this._index = at;
      this._paint();
    }

    _paint() {
      const total = this._sources.length;
      const at = this.querySelector("[data-fs-lb-at]");
      const of = this.querySelector("[data-fs-lb-of]");
      if (at) at.textContent = String(this._index + 1);
      if (of) of.textContent = String(total);

      // Two of each step control: the side arrows and the bottom row's.
      const atEnd = (hook, done) =>
        this.querySelectorAll(hook).forEach((button) => {
          button.disabled = done;
        });
      atEnd("[data-fs-lb-prev]", this._index === 0);
      atEnd("[data-fs-lb-next]", this._index >= total - 1);

      // Spoken position goes through the section's one live region.
      const engine = this.closest("flagship-product");
      const live = engine && engine.querySelector("[data-flagship-announce]");
      if (live) live.textContent = `Media ${this._index + 1} of ${total}`;

      this._syncVideos();
    }

    _videoAt(index) {
      const slide = this._track && this._track.children[index];
      return slide ? slide.querySelector("video") : null;
    }

    /** Exactly one video runs: the one being looked at. */
    _syncVideos() {
      if (!this._track) return;
      Array.from(this._track.children).forEach((slide, i) => {
        const video = slide.querySelector("video");
        if (!video) return;
        if (i === this._index) this._playVideo(video);
        else if (!video.paused) video.pause();
      });
    }

    // Same contract as section H: the muted property is what iOS checks, and a
    // refusal (Low Power Mode and friends) leaves the poster up under the play
    // badge until a real gesture lifts the restriction. Attempts can overlap
    // (the settled re-seat asks again before the first one has answered) and
    // they do not settle in order, so a rejection only counts as a refusal if
    // the video is still stopped by the time it arrives.
    _playVideo(video) {
      video.muted = true;
      const played = video.play();
      if (!played) return;
      const cue = (on) => {
        const slide = video.closest(".fs-lb-slide");
        if (slide) slide.classList.toggle("video-blocked", on);
        if (on) this._blocked.add(video);
        else this._blocked.delete(video);
      };
      played.then(() => cue(false)).catch(() => cue(video.paused));
    }

    _retryBlocked() {
      const video = this._videoAt(this._index);
      if (video && this._blocked.has(video)) this._playVideo(video);
    }

    _resetZoom() {
      this._scale = 1;
      this._panX = 0;
      this._panY = 0;
      this._applyZoom();
    }

    _applyZoom() {
      const slide = this._track && this._track.children[this._index];
      const zoom = this._zoomAt(this._index);
      if (!slide || !zoom) return;
      const zoomed = this._scale > 1;
      slide.classList.toggle("is-zoomed", zoomed);
      zoom.style.transform = zoomed
        ? `translate(${this._panX}px, ${this._panY}px) scale(${this._scale})`
        : "";
    }

    /** Keep the panned picture from leaving its own frame. */
    _clampPan() {
      const zoom = this._zoomAt(this._index);
      if (!zoom || !this._track) return;
      const maxX = Math.max(
        0,
        (zoom.offsetWidth * this._scale - this._track.clientWidth) / 2,
      );
      const maxY = Math.max(
        0,
        (zoom.offsetHeight * this._scale - this._track.clientHeight) / 2,
      );
      this._panX = Math.max(-maxX, Math.min(maxX, this._panX));
      this._panY = Math.max(-maxY, Math.min(maxY, this._panY));
    }

    /** Double tap: 2.5x on the spot that was tapped, or back to the whole. */
    _toggleZoom(x, y) {
      if (this._videoAt(this._index)) return;
      if (this._scale > 1) {
        this._resetZoom();
        return;
      }
      const zoom = this._zoomAt(this._index);
      if (!zoom) return;
      const box = zoom.getBoundingClientRect();
      const scale = 2.5;
      // A point `u` from the centre lands at `pan + scale * u`; holding it
      // still means pan = u * (1 - scale).
      this._scale = scale;
      this._panX = (x - (box.left + box.width / 2)) * (1 - scale);
      this._panY = (y - (box.top + box.height / 2)) * (1 - scale);
      this._clampPan();
      this._applyZoom();
    }

    // One pointer handler for the lot: two pointers pinch, a drag pans while
    // zoomed and dismisses while not, and two quick taps toggle the zoom. No
    // inertia, nothing to tune.
    _bindGestures(signal) {
      const track = this._track;
      const points = new Map();
      let startDist = 0;
      let startScale = 1;
      let startX = 0;
      let startY = 0;
      let dragY = 0;
      let moved = false;
      let dismissing = false;
      let lastTapAt = 0;

      track.addEventListener(
        "pointerdown",
        (evt) => {
          points.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
          this.classList.add("is-tracking");
          if (points.size === 2) {
            const [a, b] = [...points.values()];
            startDist = Math.hypot(a.x - b.x, a.y - b.y);
            startScale = this._scale;
          } else if (points.size === 1) {
            startX = evt.clientX;
            startY = evt.clientY;
            dragY = 0;
            moved = false;
            dismissing = false;
          }
        },
        { passive: true, signal },
      );

      track.addEventListener(
        "pointermove",
        (evt) => {
          const point = points.get(evt.pointerId);
          if (!point) return;
          const dx = evt.clientX - point.x;
          const dy = evt.clientY - point.y;
          point.x = evt.clientX;
          point.y = evt.clientY;
          if (
            !moved &&
            Math.hypot(evt.clientX - startX, evt.clientY - startY) > 8
          ) {
            moved = true;
          }

          if (points.size >= 2 && startDist) {
            if (this._videoAt(this._index)) return;
            const [a, b] = [...points.values()];
            const ratio = Math.hypot(a.x - b.x, a.y - b.y) / startDist;
            this._scale = Math.max(1, Math.min(4, startScale * ratio));
            this._clampPan();
            this._applyZoom();
            return;
          }
          if (!moved) return;

          if (this._scale > 1) {
            this._panX += dx;
            this._panY += dy;
            this._clampPan();
            this._applyZoom();
            return;
          }

          // Swipe down to dismiss. The slide's touch-action leaves the
          // vertical axis to us, so this never fights the track's scroll.
          if (
            dismissing ||
            evt.clientY - startY > Math.abs(evt.clientX - startX) + 8
          ) {
            dismissing = true;
            dragY = Math.max(0, evt.clientY - startY);
            const zoom = this._zoomAt(this._index);
            if (zoom) zoom.style.transform = `translateY(${dragY}px)`;
          }
        },
        { passive: true, signal },
      );

      const release = (evt) => {
        if (!points.delete(evt.pointerId)) return;
        if (points.size) {
          // A finger came off a pinch: re-seat the drag origin on the one left.
          const [rest] = [...points.values()];
          startX = rest.x;
          startY = rest.y;
          startDist = 0;
          moved = true;
          return;
        }
        this.classList.remove("is-tracking");
        startDist = 0;

        if (dismissing) {
          dismissing = false;
          if (dragY > 90) {
            this.close();
          } else {
            const zoom = this._zoomAt(this._index);
            if (zoom) zoom.style.transform = "";
          }
          dragY = 0;
          return;
        }

        if (moved) return;
        if (evt.timeStamp - lastTapAt < 300) {
          lastTapAt = 0;
          this._toggleZoom(evt.clientX, evt.clientY);
        } else {
          lastTapAt = evt.timeStamp;
        }
      };

      track.addEventListener("pointerup", release, { passive: true, signal });
      track.addEventListener("pointercancel", release, {
        passive: true,
        signal,
      });
    }
  }
  if (!customElements.get("flagship-lightbox")) {
    customElements.define("flagship-lightbox", FlagshipLightbox);
  }

  function initCarouselLightbox(engine) {
    const scroll = engine.querySelector(".carousel-scroll");
    const lightbox = engine.querySelector("flagship-lightbox");
    if (!scroll || !lightbox) return;
    const { signal } = engine._ac;

    let downX = 0;
    let downY = 0;
    let dragged = false;

    // One element per visible slide, in carousel order. The <video> comes
    // before its own poster fallback <img> in document order, so a video slide
    // yields the video and an image slide yields the picture.
    function openFrom(slide) {
      const media = getVisibleSlides(engine)
        .filter((visible) => visible.hasAttribute("data-fs-lb-open"))
        .map((visible) => visible.querySelector("img, video"))
        .filter(Boolean);
      const at = media.indexOf(slide.querySelector("img, video"));
      if (at >= 0) lightbox.open(media, at, slide);
    }

    scroll.addEventListener(
      "pointerdown",
      (evt) => {
        downX = evt.clientX;
        downY = evt.clientY;
        dragged = false;
      },
      { passive: true, signal },
    );
    scroll.addEventListener(
      "pointermove",
      (evt) => {
        if (
          !dragged &&
          Math.hypot(evt.clientX - downX, evt.clientY - downY) > 8
        ) {
          dragged = true;
        }
      },
      { passive: true, signal },
    );

    // A tap opens the slide; a drag past 8px (the sticky bar's threshold) only
    // scrolls the carousel. detail === 0 is a keyboard-generated click, which
    // never follows a pointer, so a stale drag must not swallow it.
    scroll.addEventListener(
      "click",
      (evt) => {
        const slide = evt.target.closest("[data-fs-lb-open]");
        if (!slide || (dragged && evt.detail)) return;
        openFrom(slide);
      },
      { signal },
    );

    // The slides are role="button", and Enter on one of those fires no click.
    scroll.addEventListener(
      "keydown",
      (evt) => {
        if (evt.key !== "Enter" && evt.key !== " ") return;
        const slide = evt.target.closest("[data-fs-lb-open]");
        if (!slide) return;
        evt.preventDefault();
        openFrom(slide);
      },
      { signal },
    );
  }
})();
